import React, { useMemo, useState } from "react";
import { Spinner, Tooltip, OverlayTrigger, Card } from "react-bootstrap";
import Accordion from "react-bootstrap/Accordion";
import "react-datepicker/dist/react-datepicker.css";
import BreadCrumb from "../Common/BreadCrumb";
import NavHeader from "../Common/NavHeader";
import {
  useGetReportStatus,
  useStopScheduler,
  useStopTask,
  useStartTask,
} from "./api";
import { Modal } from "react-bootstrap";
import {
  ExclamationTriangle,
  ExclamationCircleFill,
  TerminalFill,
  PlayBtnFill,
  EyeFill,
} from "react-bootstrap-icons";
import { formatDate } from "../Utility/formatDate";
import { ArrowRepeat } from "react-bootstrap-icons";
import { useCalculateAndUpdateGrandTotal } from "../SalesReceipt/api";
import { queryClient } from "../../App";
import { ToastContainer, toast } from "react-toastify";
import "../../App.css";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import LogsModal from "./LogsModal";
import { CircleFill } from "react-bootstrap-icons";
import ScreenShotsModal from "./ScreenshotsModal";
import TaskLogsModal from "./TaskLogsModal";
import SyncLogsModal from "./SyncLogsModal";
import ColumnFilters from "./enhancements/ColumnFilters";
import CalendricView from "./enhancements/CalendricView";
import PosStatus from "./PosStatus";

import { PencilFill } from "react-bootstrap-icons";
import { useUpdateJobStatus, getTaskScreenshots } from "./api";

function formatDateOne(inputDate) {
  const date = new Date(inputDate);
  if (isNaN(date)) {
    return "Invalid Date";
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

const MasterReportStatus = () => {
  const [editingJobTaskId, setEditingJobTaskId] = useState(null);
  const [selectedNewStatus, setSelectedNewStatus] = useState("");
  const { mutate: updateJobStatus, isPending: updatingJobStatus } = useUpdateJobStatus();

  const [startDate, setStartDate] = useState(new Date());
  // phones only render the selected day (see .mrs-day), so only fetch that one
  // ponytail: read once per render, a resize alone won't refetch
  const isPhone = window.matchMedia("(max-width: 767.98px)").matches;
  const { isLoading, data } = useGetReportStatus(formatDateOne(startDate), isPhone);
  const [taskIdToStop, setTaskIdToStop] = useState(null);
  const [taskIdToStart, setTaskIdToStart] = useState(null);
  const [schedulerIdToStop, setSchedulerIdToStop] = useState(null);
  const [jobIdForLogs, setJobIdForLogs] = useState(null);
  const [taskIdForLogs, setTaskIdForLogs] = useState(null);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [showTaskLogsModal, setShowTaskLogsModal] = useState(false);
  const [showScreenShotsModal, setShowScreenShotsModal] = useState(false);
  const [showSyncLogsModal, setShowSyncLogsModal] = useState(false);
  const [syncLogs, setSyncLogs] = useState(false);
  const [screenshots, setScreenshots] = useState([]);
  const openScreenshots = async (taskId) => {
    try {
      const urls = await getTaskScreenshots(taskId);
      if (!urls?.length) return toast.info("No screenshots found for this task");
      setScreenshots(urls);
      setShowScreenShotsModal(true);
    } catch (e) {
      toast.error("Failed to load screenshots");
    }
  };

  // New State for Enhancements
  const [searchQueries, setSearchQueries] = useState({});
  const [posFilters, setPosFilters] = useState({});
  const [showUnbalancedOnly, setShowUnbalancedOnly] = useState({});
  const [hideStoppedJobs, setHideStoppedJobs] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const showCalendricView = searchParams.get('view') === 'calendric';
  const navigate = useNavigate();
  const { mutate: stopTask, isPending: stoppingTask } = useStopTask();
  const {
    mutate: calculateAndUpdateGrandTotal,
    isPending: calculatingAndUpdateGrandTotal,
  } = useCalculateAndUpdateGrandTotal();
  const { mutate: stopScheduler, isPending: stoppingScheduler } =
    useStopScheduler();

  const { mutate: startTask, isPending: startingTask } = useStartTask();

  const handleStartTask = (task_id) => {
    startTask({ task_id });
  };

  const handleStopTask = (task_id) => {
    stopTask({ task_id });
  };

  const handleStopScheduler = (scheduler_id) => {
    stopScheduler({ scheduler_id });
  };

  function isReportSynced(sync_status) {
    const clickbaconSynced = sync_status.clickbacon_sync.some(
      (item) => item.sync_status === true
    );

    const qboSynced = sync_status.qbo_sync.some(
      (item) => item.sync_status === true
    );

    return clickbaconSynced || qboSynced;
  }

  function findTheDateOnWhichReportIsSynced(sync_status) {
    const sync_dates = [];
    const clickbaconSynced = sync_status.clickbacon_sync.some(
      (item) => item.sync_status === true
    );

    const qboSynced = sync_status.qbo_sync.some(
      (item) => item.sync_status === true
    );

    if (clickbaconSynced) {
      sync_status.clickbacon_sync.forEach((item) => {
        if (item.sync_status === true) {
          sync_dates.push(item.sync_date);
        }
      });
    }

    if (qboSynced) {
      sync_status.qbo_sync.forEach((item) => {
        if (item.sync_status === true) {
          sync_dates.push(item.sync_date);
        }
      });
    }
    let date = sync_dates.sort((a, b) => new Date(b) - new Date(a))[0];
    return new Date(date).toISOString().split("T")[0];
  }

  function findSyncModeAndSyncedTo(sync_status) {
    let arrayForSyncMode = [];
    const clickbaconSynced = sync_status.clickbacon_sync.some(
      (item) => item.sync_status === true
    );

    const qboSynced = sync_status.qbo_sync.some(
      (item) => item.sync_status === true
    );

    if (clickbaconSynced) {
      sync_status.clickbacon_sync.forEach((item) => {
        if (item.sync_status === true) {
          arrayForSyncMode.push(item);
        }
      });
    }

    if (qboSynced) {
      sync_status.qbo_sync.forEach((item) => {
        if (item.sync_status === true) {
          arrayForSyncMode.push(item);
        }
      });
    }

    let sync_mode = arrayForSyncMode.sort(
      (a, b) => new Date(b) - new Date(a)
    )[0];
    sync_mode = sync_mode?.["sync_logs"].split("~")[1];
    sync_mode = sync_mode?.replace(/[\[\]']+/g, "");

    if (clickbaconSynced) {
      return { syncedTo: "clickBACON", syncMode: sync_mode };
    } else if (qboSynced) {
      return { syncedTo: "QBO", syncMode: sync_mode };
    } else {
      return { syncedTo: "N/A", syncMode: "N/A" };
    }
  }

  const availablePosOptions = useMemo(() => {
    if (!data?.data) return {};
    const options = {};
    Object.keys(data.data).forEach(date => {
      const posSet = new Set();
      Object.values(data.data[date]).forEach(reports => {
        reports.forEach(report => {
          report.scraper_jobs?.forEach(job => {
            if (job.scheduler_details?.pos_name) posSet.add(job.scheduler_details.pos_name);
          });
        });
      });
      options[date] = Array.from(posSet);
    });
    return options;
  }, [data]);

  const filteredDataKeys = useMemo(() => {
    if (!data?.data) return {};
    const result = {};
    Object.keys(data.data).forEach(date => {
      const query = (searchQueries[date] || "").toLowerCase();
      const posFilter = posFilters[date] || [];

      result[date] = Object.keys(data.data[date]).filter(rest_id => {
        const restName = (data.id_name_map?.[rest_id] || "").toLowerCase();

        if (query && !restName.includes(query)) return false;

        if (posFilter.length > 0) {
          const reports = data.data[date][rest_id] || [];
          let hasMatchingPos = false;
          reports.forEach(report => {
            report.scraper_jobs?.forEach(job => {
              if (posFilter.includes(job.scheduler_details?.pos_name)) hasMatchingPos = true;
            });
          });
          if (!hasMatchingPos) return false;
        }

        return true;
      });
    });
    return result;
  }, [data, searchQueries, posFilters, data?.id_name_map]);

  const failedReports = useMemo(() => {
    if (!data) return [];

    return Object.keys(data.data).flatMap((date) => {
      return Object.keys(data.data[date]).flatMap((restaurant) => {
        return data.data[date][restaurant].filter((report) => {
          return (
            (report.report_status?.some(
              (it) => it.report_status.toLowerCase() === "failed"
            ) ||
              report.scraper_jobs?.some(
                (it) => it.job_status.toLowerCase() === "failed"
              ))
          )
        });
      });
    });
  }, [data]);


  const warningReports = useMemo(() => {
    if (!data) return [];

    return Object.keys(data.data).flatMap((date) => {
      return Object.keys(data.data[date]).flatMap((restaurant) => {
        return data.data[date][restaurant].filter((report) => {
          return (
            !isReportSynced(report.sync_status)

          );
        });
      });
    });
  }, [data]);

  const successfulReports = useMemo(() => {
    if (!data) return [];

    return Object.keys(data.data).flatMap((date) => {
      return Object.keys(data.data[date]).flatMap((restaurant) => {
        return data.data[date][restaurant].filter((report) => {
          return (
            !report.report_status?.some(
              (it) => it.report_status.toLowerCase() === "failed"
            ) &&
            isReportSynced(report.sync_status) &&
            !report.scraper_jobs?.some(
              (it) => it.job_status.toLowerCase() === "failed"
            )
          ) &&
            report?.net_sales > 0
        });
      });
    });
  }, [data]);

  const reportsWithZeroNetSales = useMemo(() => {
    if (!data) return [];

    return Object.keys(data.data).flatMap((date) => {
      return Object.keys(data.data[date]).flatMap((restaurant) => {
        return data.data[date][restaurant].filter((report) => {
          return (report?.net_sales == 0 || !report?.net_sales) && report?.scraper_jobs?.every((it) => it.job_status.toLowerCase() == "completed");
        });
      });
    });
  }, [data]);

  console.log("reportsWithZeroNetSales", reportsWithZeroNetSales);


  const findStatus = (report_status, classified_flag, report_name) => {
    if (report_status.toLowerCase() === "completed") {
      if (
        ["product", "labor", "discount"].includes(report_name.toLowerCase())
      ) {
        if (classified_flag) {
          return "Completed";
        } else {
          return "Downloaded";
        }
      } else {
        return "Completed";
      }
    } else {
      return report_status;
    }
  };

  const renderRestaurantAccordion = (rest_id, i, date) => {
    const reports = data?.data?.[date]?.[rest_id] || [];

    const hasFailedReports = reports.some((report) =>
      report["report_status"]?.some((it) => it.report_status.toLowerCase() === "failed")
    );

    const hasFailedScraperJobs = reports.some((report) =>
      report["scraper_jobs"]?.some((it) => it.job_status.toLowerCase() === "failed")
    );

    const hasUnsyncedReports = reports.some((report) => !isReportSynced(report["sync_status"]));

    const hasUnbalanced = reports.some((report) => {
      const gt = report["grand_total"];
      return gt === "NA" || (gt !== null && gt !== undefined && !isNaN(gt) && Number(gt) !== 0);
    });

    return (
      <Accordion defaultActiveKey={i} className="my-2 h-75">
        <Accordion.Item eventKey="0" style={{ border: {} }}>
          <Accordion.Header title={data.id_name_map?.[rest_id]}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: "13px" }}>
                {data.id_name_map?.[rest_id]?.length > 17
                  ? data.id_name_map?.[rest_id]?.slice(0, 17) + "..."
                  : data.id_name_map?.[rest_id]}
              </span>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginRight: "10px",
                }}
              >
                {hasFailedReports && (
                  <CircleFill fill="#d0b097" className="mx-2" height={10} width={10} title="Failed Reports" />
                )}
                {hasFailedScraperJobs && (
                  <CircleFill fill="#a57b7c" className="mx-2" height={10} width={10} title="Failed Scraper Jobs" />
                )}
                {hasUnsyncedReports && (
                  <CircleFill fill="#a15243" className="mx-1" height={10} width={10} title="Unsynced Reports" />
                )}
                {hasUnbalanced && (
                  <CircleFill fill="#413b47" className="mx-1" height={10} width={10} title="Grand Total Not Zero" />
                )}
              </div>
            </div>
          </Accordion.Header>
          <Accordion.Body style={{ textAlign: "left" }}>
            {reports.map((report, rindx) => {
              const hasReportStatus = report["report_status"]?.length > 0;
              return (
                <div key={rindx}>
                  {hasReportStatus && (
                    <p className="fw-bold">
                      <Link
                        className="text-body"
                        style={{
                          textDecoration: "underline",
                        }}
                        to={`/restaurants/${rest_id}/jhbadhbjasd/sales-receipt/?date=${date}&restaurant_name=${data.id_name_map?.[rest_id]}`}
                        target="_blank"
                      >
                        Report Status
                      </Link>
                    </p>
                  )}
                  <div>
                    {report["report_status"]?.map((r, ri) => {
                      const statusVal = findStatus(r["report_status"], r["classified_flag"], r["report_name"]);
                      const backgroundColor =
                        statusVal === "Completed"
                          ? "#348355"
                          : statusVal === "Downloaded"
                          ? "linear-gradient(to right, #348355 50%, #DCAF0D 50%)"
                          : statusVal === "Failed"
                          ? "#D14E4E"
                          : "#DCAF0D";

                      const pillStyle = {
                        background: backgroundColor,
                        color: "white",
                        fontSize: "12px",
                        padding: "2px 8px 2px 8px",
                        borderRadius: 10,
                      };

                      return (
                        <div
                          key={ri}
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "space-between",
                          }}
                        >
                          <p>{r["report_name"]}</p>
                          <p
                            style={pillStyle}
                            title={statusVal === "Downloaded" ? "Classification Pending" : statusVal}
                          >
                            {statusVal}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                  {report["report_status"]?.filter((it) => it["report_status"]?.toLowerCase() !== "failed").length > 0 && (
                    <hr className="featurette-divider my-2" />
                  )}
                  <p className="fw-bold"> Sync Status</p>
                  <div className="d-flex justify-content-between">
                    <p>Sync Status</p>
                    <p
                      style={{
                        backgroundColor: isReportSynced(report["sync_status"]) ? "#348355" : "#DCAF0D",
                        color: "white",
                        fontSize: "12px",
                        padding: "2px 8px 2px 8px",
                        borderRadius: 10,
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        let qboLogs = report["sync_status"]["qbo_sync"];
                        let clickbaconLogs = report["sync_status"]["clickbacon_sync"];
                        let logs = qboLogs.concat(clickbaconLogs);
                        if (logs.length > 0) {
                          setShowSyncLogsModal(true);
                          setSyncLogs(logs);
                        }
                      }}
                    >
                      {isReportSynced(report["sync_status"]) ? "Synced" : "Pending"}
                    </p>
                  </div>
                  <div className="d-flex justify-content-between">
                    <p>Synced At</p>
                    <p
                      style={{
                        backgroundColor: isReportSynced(report["sync_status"]) ? "#348355" : "#DCAF0D",
                        color: "white",
                        fontSize: "12px",
                        padding: "2px 8px 2px 8px",
                        borderRadius: 10,
                      }}
                    >
                      {isReportSynced(report["sync_status"])
                        ? findTheDateOnWhichReportIsSynced(report["sync_status"])
                        : "N/A"}
                    </p>
                  </div>
                  <div className="d-flex justify-content-between">
                    <p>Sync Mode</p>
                    <p
                      style={{
                        backgroundColor:
                          findSyncModeAndSyncedTo(report["sync_status"])["syncMode"] !== "N/A"
                            ? "#348355"
                            : "#DCAF0D",
                        color: "white",
                        fontSize: "12px",
                        padding: "2px 8px 2px 8px",
                        borderRadius: 10,
                      }}
                    >
                      {findSyncModeAndSyncedTo(report["sync_status"])["syncMode"]}
                    </p>
                  </div>
                  <div className="d-flex justify-content-between">
                    <p>Synced To</p>
                    <p
                      style={{
                        backgroundColor:
                          findSyncModeAndSyncedTo(report["sync_status"])["syncMode"] !== "N/A"
                            ? "#348355"
                            : "#DCAF0D",
                        color: "white",
                        fontSize: "12px",
                        padding: "2px 8px 2px 8px",
                        borderRadius: 10,
                      }}
                    >
                      {findSyncModeAndSyncedTo(report["sync_status"])["syncedTo"]}
                    </p>
                  </div>
                  <hr className="featurette-divider" />
                  <div className="d-flex justify-content-between">
                    <p>Grand Total</p>
                    <p
                      style={{
                        backgroundColor: report["grand_total"] === 0 ? "#348355" : "#DCAF0D",
                        color: "white",
                        fontSize: "12px",
                        padding: "2px 8px 2px 8px",
                        borderRadius: 10,
                      }}
                    >
                      {report["grand_total"]}
                      <span
                        style={{
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          const body = {
                            restaurant_id: rest_id,
                            report_date: date,
                          };
                          calculateAndUpdateGrandTotal(body, {
                            onSuccess: () => {
                              queryClient.invalidateQueries({ queryKey: ["master-report-status", date] });
                            },
                          });
                        }}
                      >
                        <ArrowRepeat
                          className={`mx-1 ${calculatingAndUpdateGrandTotal ? "spin-animation" : ""}`}
                          style={{
                            height: 15,
                            width: 15,
                          }}
                        />
                      </span>
                    </p>
                  </div>

                  <hr className="featurette-divider" />
                  <div className="d-flex justify-content-between">
                    <p>Net Sales</p>
                    <p
                      style={{
                        backgroundColor: report?.["net_sales"] > 0 ? "#348355" : "#DCAF0D",
                        color: "white",
                        fontSize: "12px",
                        padding: "2px 8px 2px 8px",
                        borderRadius: 10,
                      }}
                    >
                      {report?.["net_sales"] > 0 ? report["net_sales"] : "No Sales"}
                    </p>
                  </div>

                  <hr className="featurette-divider" />
                  <span className="fw-bold">Scraper Jobs</span>
                  {report?.["scraper_jobs"]?.map((job, ji) => {
                    const isJobInProgressOrScheduled =
                      job.job_status === "in progress" || job.job_status.toLowerCase() === "scheduled";
                    const isJobStopped = job.job_status === "stopped";
                    const jobBorder = isJobInProgressOrScheduled
                      ? "2px solid #DCAF0D"
                      : isJobStopped
                      ? "2px solid #D14E4E"
                      : "2px solid #348355";

                    return (
                      <Card key={ji} className="mb-2">
                        <Card.Header
                          style={{
                            textTransform: "capitalize",
                            border: jobBorder,
                            borderCollapse: "collapse",
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-center">
                            {job?.task_details?.schedule_type} - {job?.scheduler_details?.pos_name}
                            <span>
                              <TerminalFill
                                title="View Job Logs"
                                className="mx-2"
                                fill="gray"
                                style={{
                                  cursor: "pointer",
                                }}
                                onClick={() => {
                                  setTaskIdForLogs(job.task_id);
                                  setShowTaskLogsModal(true);
                                }}
                              />
                              {job.job_status !== "in progress" &&
                                new Date(`${job?.task_details?.execution_date} ${job?.task_details?.execution_time}`) <
                                  new Date() && (
                                  <ArrowRepeat
                                    title="Retry Job"
                                    style={{
                                      cursor: "pointer",
                                    }}
                                    onClick={() => {
                                      setTaskIdToStart(job.task_id);
                                    }}
                                  />
                                )}
                              {job?.task_details?.screenshot?.length > 0 &&
                                job?.task_details.screenshot.every((it) => it !== null) && (
                                  <EyeFill
                                    title="View Screenshots"
                                    className="mx-2"
                                    fill="gray"
                                    style={{
                                      cursor: "pointer",
                                    }}
                                    onClick={() => {
                                      openScreenshots(job.task_id);
                                    }}
                                  />
                                )}
                            </span>
                          </div>
                        </Card.Header>
                        <Card.Body
                          style={{
                            padding: 7,
                            border: jobBorder,
                            borderCollapse: "collapse",
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-center">
                            <p className="mb-0">Job Status</p>
                            <div className="d-flex align-items-center gap-2">
                              {editingJobTaskId === job.task_id ? (
                                <>
                                  <select
                                    className="form-select form-select-sm"
                                    style={{ fontSize: "12px", width: "130px" }}
                                    value={selectedNewStatus}
                                    onChange={(e) => setSelectedNewStatus(e.target.value)}
                                  >
                                    <option value="" disabled>Select status</option>
                                    {["in progress", "scheduled", "completed", "failed", "stopped"].map((s) => (
                                      <option key={s} value={s}>{s}</option>
                                    ))}
                                  </select>
                                  <button
                                    className="btn btn-success btn-sm py-0 px-2"
                                    style={{ fontSize: "11px" }}
                                    disabled={!selectedNewStatus || updatingJobStatus}
                                    onClick={() => {
                                      updateJobStatus(
                                        {
                                          task_id: job.task_id,
                                          job_status: selectedNewStatus,
                                          report_date: date,
                                          restaurant_id: rest_id,
                                        },
                                        {
                                          onSuccess: () => {
                                            queryClient.invalidateQueries({ queryKey: ["master-report-status", date] });
                                            setEditingJobTaskId(null);
                                            setSelectedNewStatus("");
                                          },
                                        }
                                      );
                                    }}
                                  >
                                    {updatingJobStatus ? "..." : "Save"}
                                  </button>
                                  <button
                                    className="btn btn-secondary btn-sm py-0 px-2"
                                    style={{ fontSize: "11px" }}
                                    onClick={() => {
                                      setEditingJobTaskId(null);
                                      setSelectedNewStatus("");
                                    }}
                                  >
                                    ✕
                                  </button>
                                </>
                              ) : (
                                <>
                                  <p className="mb-0" style={{ textTransform: "capitalize" }}>
                                    {job["job_status"]}
                                  </p>
                                  <PencilFill
                                    title="Edit Job Status"
                                    size={11}
                                    fill="gray"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => {
                                      setEditingJobTaskId(job.task_id);
                                      setSelectedNewStatus(job["job_status"]);
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </div>
                          <div className="d-flex justify-content-between">
                            <p>Start Time</p>
                            <p style={{ textTransform: "capitalize" }}>{job["job_start_time"]}</p>
                          </div>
                          <div className="d-flex justify-content-between">
                            <p>End Time</p>
                            <p style={{ textTransform: "capitalize" }}>{job["job_end_time"]}</p>
                          </div>
                          <div className="d-flex justify-content-between">
                            <p>Extraction Type</p>
                            <p style={{ textTransform: "capitalize" }}>{job["task_details"]?.["extraction_type"]}</p>
                          </div>
                          <div className="d-flex justify-content-between">
                            <p>Execution Date</p>
                            <p style={{ textTransform: "capitalize" }}>{job["task_details"]?.["execution_date"]}</p>
                          </div>
                          <div className="d-flex justify-content-between">
                            <p>Execution Time</p>
                            <p style={{ textTransform: "capitalize" }}>{job["task_details"]?.["execution_time"]}</p>
                          </div>
                        </Card.Body>
                      </Card>
                    );
                  })}
                </div>
              );
            })}
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    );
  };

  return (
    <>
      <ToastContainer />
      <NavHeader view="" />
      <div className="mx-2 mx-md-4" style={{ marginTop: "100px" }}>
        <BreadCrumb
          text="Home > Master Report Status"
          showDatePicker={true}
          startDate={startDate}
          setStartDate={setStartDate}
          showSimplifiedViewLink={true}
          showCalendricViewLink={true}
          isCalendricViewActive={showCalendricView}
          onCalendricViewToggle={() => {
            if (showCalendricView) {
              const newParams = new URLSearchParams(searchParams);
              newParams.delete('view');
              setSearchParams(newParams);
            } else {
              const newParams = new URLSearchParams(searchParams);
              newParams.set('view', 'calendric');
              setSearchParams(newParams);
            }
          }}
        />
        <p className="text-left mt-2 mx-5 text-muted flex items-center">
          Note:
          <CircleFill size={15} fill="#a57b7c" className="mx-1" /> Failed
          Scraper Jobs, <CircleFill size={15} fill="#d0b097" className="mx-1" />{" "}
          Failed Reports
          <CircleFill size={15} fill="#413b47" className="mx-1" /> Grand Total
          Not Zero <CircleFill size={15} fill="#a15243" className="mx-1" />{" "}
          Unsynced Report{" "}
        </p>
        {isLoading ? (
          <div className="d-flex justify-content-center align-items-center">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : showCalendricView ? (
          <div className="mt-4 w-100">
            <CalendricView
              startDate={startDate}
              onDateClick={(date) => {
                const [y, m, d] = date.split('-').map(Number);
                setStartDate(new Date(y, m - 1, d));
                const newParams = new URLSearchParams(searchParams);
                newParams.delete('view');
                setSearchParams(newParams);
              }}
            />
          </div>
        ) : (
          <div className="px-md-4 mb-4">
            <div className="d-flex mt-2 gap-4 ">
              {data &&
                Object.keys(data?.data)?.map((date, index) => {
                  let myStyle = {};
                  let isCurrent =
                    date == new Date().toISOString().split("T")[0];
                  let isSelected = date == formatDateOne(startDate);
                  isCurrent
                    ? (myStyle = {
                      backgroundColor: "#348355",
                      color: "white",
                      border: "20px solid #348355",
                      height: "50px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    })
                    : isSelected
                      ? (myStyle = {
                        backgroundColor: "#E0E0E0",
                        color: "#000000",
                        border: "2px solid #348355",
                        height: "50px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      })
                      : (myStyle = {
                        backgroundColor: "#E0E0E0",
                        color: "#000000",
                        border: "1px solid #E0E0E0",
                        height: "50px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      });

                  const colFilteredRestIds = filteredDataKeys[date] || [];
                  const colFailedRestIds = [];
                  const colWarningRestIds = [];
                  const colAllWarningRestIds = [];
                  const colSuccessfulRestIds = [];
                  const colNoSalesRestIds = [];
                  const colEmailSubRestIds = [];
                  const colManuallyUploadedRestIds = [];
                  const colUnbalancedRestIds = [];
                  const colUnsyncedRestIds = [];

                  colFilteredRestIds.forEach(rest_id => {
                    const reports = data.data[date][rest_id] || [];

                    const isEmailSub = reports.some(report => report.category === "Email Subscription");
                    if (isEmailSub) {
                      colEmailSubRestIds.push(rest_id);
                      return;
                    }

                    const isManuallyUploaded = reports.some(report => report.category === "Manually Uploaded");
                    if (isManuallyUploaded) {
                      colManuallyUploadedRestIds.push(rest_id);
                      return;
                    }

                    const hasFailedReports = reports.some((report) =>
                      report["report_status"]?.some((it) => it.report_status.toLowerCase() === "failed")
                    );
                    const hasFailedScraperJobs = reports.some((report) =>
                      report["scraper_jobs"]?.some((it) => it.job_status.toLowerCase() === "failed")
                    );
                    const isFailed = hasFailedReports || hasFailedScraperJobs;

                    if (isFailed) {
                      colFailedRestIds.push(rest_id);
                      return;
                    }

                    const hasUnsyncedReports = reports.some((report) => !isReportSynced(report["sync_status"]));
                    const hasUnbalanced = reports.some((report) => {
                      const gt = report["grand_total"];
                      return gt === "NA" || (gt !== null && gt !== undefined && !isNaN(gt) && Number(gt) !== 0);
                    });

                    if (hasUnsyncedReports) {
                      colAllWarningRestIds.push(rest_id);
                      colWarningRestIds.push(rest_id);
                      if (hasUnbalanced) {
                        colUnbalancedRestIds.push(rest_id);
                      } else {
                        colUnsyncedRestIds.push(rest_id);
                      }
                      return;
                    }

                    const isNoSales = reports.every((report) =>
                      (report?.net_sales == 0 || !report?.net_sales) &&
                      report?.scraper_jobs?.every((it) => it.job_status.toLowerCase() == "completed")
                    );

                    if (isNoSales && reports.length > 0) {
                      colNoSalesRestIds.push(rest_id);
                      return;
                    }

                    const isSuccessful = reports.every((report) =>
                      !report.report_status?.some((it) => it.report_status.toLowerCase() === "failed") &&
                      isReportSynced(report.sync_status) &&
                      !report.scraper_jobs?.some((it) => it.job_status.toLowerCase() === "failed")
                    ) && reports.some(report => report?.net_sales > 0);

                    if (isSuccessful) {
                      colSuccessfulRestIds.push(rest_id);
                    }
                  });

                  return (
                    <div
                      className={
                        "mrs-day col-sm flex-grow-1 text-sm-start rounded-2 border" +
                        // phones show only the selected day; the date arrows step through days
                        (isSelected ? "" : " d-none d-md-block")
                      }
                      key={index}
                    >
                      <p
                        className={
                          "text-sm-center pt-2  rounded-top-2" +
                          (isCurrent ? "" : " date-header-neutral")
                        }
                        style={myStyle}
                      >
                        {date}
                      </p>

                      <ColumnFilters
                        searchQuery={searchQueries[date] || ""}
                        onSearchChange={(val) => setSearchQueries(prev => ({ ...prev, [date]: val }))}
                        posOptions={availablePosOptions[date] || []}
                        selectedPos={posFilters[date] || []}
                        onPosChange={(val) => setPosFilters(prev => ({ ...prev, [date]: val }))}
                      />

                      {/* For dates older than the current date */}
                      {new Date(date) < new Date(formatDateOne(new Date())) ? (
                        <div
                          style={{ height: "60vh", overflow: "auto" }}
                          className="hide-scrollbar"
                        >
                          {colFailedRestIds.length > 0 && (
                            <div className="text-sm-center px-1  ">
                              <Accordion defaultActiveKey="1" className="my-2" style={{ "--category-accent": "#d14e4e" }}>
                                <Accordion.Item eventKey="1">
                                  <Accordion.Header className="custom-header-danger">
                                    <div>Failed Reports ({colFailedRestIds.length})</div>
                                  </Accordion.Header>
                                  <Accordion.Body
                                    mountOnEnter
                                    unmountOnExit
                                    style={{
                                      padding: 0,
                                      textAlign: "left",
                                    }}
                                  >
                                    <div>
                                      {data &&
                                        Object.keys(data?.data[date])?.length ==
                                        0 ? (
                                        <div
                                          className="d-flex justify-content-center align-items-center "
                                          style={{ height: "60vh" }}
                                        >
                                          <p
                                            className="text-center "
                                            style={{ paddingLeft: 8 }}
                                          >
                                            These Dates are not accessible right
                                            now.
                                          </p>
                                        </div>
                                      ) : (
                                        colFailedRestIds.map(
                                          (rest_id, i) => {
                                            const hasFailedReports = data?.data[
                                              date
                                            ][rest_id]?.some((report) =>
                                              report["report_status"]?.some(
                                                (it) =>
                                                  it.report_status.toLowerCase() ===
                                                  "failed"
                                              )
                                            );

                                            const hasFailedScraperJobs =
                                              data?.data[date][rest_id]?.some(
                                                (report) =>
                                                  report["scraper_jobs"]?.some(
                                                    (it) =>
                                                      it.job_status.toLowerCase() ===
                                                      "failed"
                                                  )
                                              );



                                            return (
                                              <div className="mx-2" key={i}>
                                                {(hasFailedReports ||
                                                  hasFailedScraperJobs) &&
                                                  (
                                                    <Accordion
                                                      defaultActiveKey={rest_id}
                                                      className="my-2 h-75"
                                                    >
                                                      <Accordion.Item eventKey="0">
                                                        <Accordion.Header
                                                          title={
                                                            data.id_name_map?.[
                                                            rest_id
                                                            ]
                                                          }
                                                        >
                                                          <div
                                                            style={{
                                                              display: "flex",
                                                              justifyContent:
                                                                "space-between",
                                                              width: "100%",
                                                              alignItems:
                                                                "center",
                                                            }}
                                                          >
                                                            <span
                                                              className=""
                                                              style={{
                                                                fontSize:
                                                                  "13px",
                                                              }}
                                                            >
                                                              {data
                                                                .id_name_map?.[
                                                                rest_id
                                                              ].length > 17
                                                                ? data.id_name_map?.[
                                                                  rest_id
                                                                ]?.slice(
                                                                  0,
                                                                  17
                                                                ) + "..."
                                                                : data
                                                                  .id_name_map?.[
                                                                rest_id
                                                                ]}
                                                            </span>
                                                            <div
                                                              style={{
                                                                display: "flex",
                                                                alignItems:
                                                                  "center",
                                                                marginRight:
                                                                  "10px",
                                                              }}
                                                            >
                                                              {data?.data[date][
                                                                rest_id
                                                              ]?.some(
                                                                (report) =>
                                                                  report[
                                                                    "report_status"
                                                                  ]?.some(
                                                                    (it) =>
                                                                      it.report_status.toLowerCase() ===
                                                                      "failed"
                                                                  )
                                                              ) && (
                                                                  <CircleFill
                                                                    fill="#d0b097"
                                                                    className="mx-2"
                                                                    height={10}
                                                                    width={10}
                                                                    title="Failed Reports"
                                                                  />
                                                                )}
                                                              {data?.data[date][
                                                                rest_id
                                                              ]?.some(
                                                                (report) =>
                                                                  report[
                                                                    "scraper_jobs"
                                                                  ]?.some(
                                                                    (it) =>
                                                                      it.job_status.toLowerCase() ===
                                                                      "failed"
                                                                  )
                                                              ) && (
                                                                  <CircleFill
                                                                    fill="#a57b7c"
                                                                    className="mx-2"
                                                                    height={10}
                                                                    width={10}
                                                                    title="Failed Scraper Jobs"
                                                                  />
                                                                )}
                                                            </div>
                                                          </div>
                                                        </Accordion.Header>
                                                        <Accordion.Body
                                                          style={{
                                                            textAlign: "left",
                                                          }}
                                                        >
                                                          {data?.data[date][
                                                            rest_id
                                                          ]?.map(
                                                            (report, rindx) => {
                                                              return (
                                                                <>
                                                                  {report[
                                                                    "report_status"
                                                                  ]?.filter(
                                                                    (it) =>
                                                                      it[
                                                                        "report_status"
                                                                      ]?.toLowerCase() !==
                                                                      "failed" ||
                                                                      it[
                                                                        "report_status"
                                                                      ]?.toLowerCase() ==
                                                                      "failed"
                                                                  ).length >
                                                                    0 && (
                                                                      <p className="fw-bold">
                                                                        <Link
                                                                          className="text-body"
                                                                          style={{
                                                                            textDecoration:
                                                                              "underline",
                                                                          }}
                                                                          to={`/restaurants/${rest_id}/jhbadhbjasd/sales-receipt/?date=${date}&restaurant_name=${data.id_name_map?.[rest_id]}`}
                                                                          target="_blank"
                                                                        >
                                                                          Report
                                                                          Status
                                                                        </Link>
                                                                      </p>
                                                                    )}
                                                                  <div
                                                                    key={rindx}
                                                                  >
                                                                    {report[
                                                                      "report_status"
                                                                    ]?.map(
                                                                      (
                                                                        r,
                                                                        i
                                                                      ) => {
                                                                        const backgroundColor =
                                                                          findStatus(
                                                                            r[
                                                                            "report_status"
                                                                            ],
                                                                            r[
                                                                            "classified_flag"
                                                                            ],
                                                                            r[
                                                                            "report_name"
                                                                            ]
                                                                          ) ===
                                                                            "Completed"
                                                                            ? "#348355"
                                                                            : findStatus(
                                                                              r[
                                                                              "report_status"
                                                                              ],
                                                                              r[
                                                                              "classified_flag"
                                                                              ],
                                                                              r[
                                                                              "report_name"
                                                                              ]
                                                                            ) ===
                                                                              "Downloaded"
                                                                              ? "linear-gradient(to right, #348355 50%, #DCAF0D 50%)"
                                                                              : findStatus(
                                                                                r[
                                                                                "report_status"
                                                                                ],
                                                                                r[
                                                                                "classified_flag"
                                                                                ],
                                                                                r[
                                                                                "report_name"
                                                                                ]
                                                                              ) ===
                                                                                "Failed"
                                                                                ? "#D14E4E"
                                                                                : "#DCAF0D";

                                                                        const style =
                                                                        {
                                                                          background:
                                                                            backgroundColor,
                                                                          color:
                                                                            "white",
                                                                          fontSize:
                                                                            "12px",
                                                                          padding:
                                                                            "2px 8px 2px 8px",
                                                                          borderRadius: 10,
                                                                        };
                                                                        return (
                                                                          <div
                                                                            key={
                                                                              i
                                                                            }
                                                                            style={{
                                                                              display:
                                                                                "flex",
                                                                              flexDirection:
                                                                                "row",
                                                                              justifyContent:
                                                                                "space-between",
                                                                            }}
                                                                          >
                                                                            <p>
                                                                              {
                                                                                r[
                                                                                "report_name"
                                                                                ]
                                                                              }
                                                                            </p>
                                                                            <p
                                                                              style={
                                                                                style
                                                                              }
                                                                              title={
                                                                                findStatus(
                                                                                  r[
                                                                                  "report_status"
                                                                                  ],
                                                                                  r[
                                                                                  "classified_flag"
                                                                                  ],
                                                                                  r[
                                                                                  "report_name"
                                                                                  ]
                                                                                ) ===
                                                                                  "Downloaded"
                                                                                  ? "Classification Pending"
                                                                                  : findStatus(
                                                                                    r[
                                                                                    "report_status"
                                                                                    ],
                                                                                    r[
                                                                                    "classified_flag"
                                                                                    ],
                                                                                    r[
                                                                                    "report_name"
                                                                                    ]
                                                                                  )
                                                                              }
                                                                            >
                                                                              {findStatus(
                                                                                r[
                                                                                "report_status"
                                                                                ],
                                                                                r[
                                                                                "classified_flag"
                                                                                ],
                                                                                r[
                                                                                "report_name"
                                                                                ]
                                                                              )}
                                                                            </p>
                                                                          </div>
                                                                        );
                                                                      }
                                                                    )}
                                                                  </div>
                                                                  {report[
                                                                    "report_status"
                                                                  ]?.filter(
                                                                    (it) =>
                                                                      it[
                                                                        "report_status"
                                                                      ]?.toLowerCase() !==
                                                                      "failed"
                                                                  ).length >
                                                                    0 && (
                                                                      <hr className="featurette-divider my-2" />
                                                                    )}
                                                                  <p className="fw-bold">
                                                                    {" "}
                                                                    Sync Status
                                                                  </p>
                                                                  <div className="d-flex justify-content-between">
                                                                    <p>
                                                                      Sync
                                                                      Status
                                                                    </p>
                                                                    <p
                                                                      style={{
                                                                        backgroundColor:
                                                                          isReportSynced(
                                                                            report[
                                                                            "sync_status"
                                                                            ]
                                                                          )
                                                                            ? "#348355"
                                                                            : "#DCAF0D",
                                                                        color:
                                                                          "white",
                                                                        fontSize:
                                                                          "12px",
                                                                        padding:
                                                                          "2px 8px 2px 8px",
                                                                        borderRadius: 10,
                                                                        cursor: "pointer"
                                                                      }}

                                                                      onClick={() => {
                                                                        let qboLogs = report["sync_status"]["qbo_sync"];
                                                                        let clickbaconLogs = report["sync_status"]["clickbacon_sync"];
                                                                        let logs = qboLogs.concat(clickbaconLogs);
                                                                        if (logs.length > 0) {
                                                                          setShowSyncLogsModal(true);
                                                                          setSyncLogs(logs);

                                                                        }
                                                                      }}
                                                                    >
                                                                      {isReportSynced(
                                                                        report[
                                                                        "sync_status"
                                                                        ]
                                                                      )
                                                                        ? "Synced"
                                                                        : "Pending"}
                                                                    </p>
                                                                  </div>
                                                                  <div className="d-flex justify-content-between">
                                                                    <p>
                                                                      Synced At
                                                                    </p>
                                                                    <p
                                                                      style={{
                                                                        backgroundColor:
                                                                          isReportSynced(
                                                                            report[
                                                                            "sync_status"
                                                                            ]
                                                                          )
                                                                            ? "#348355"
                                                                            : "#DCAF0D",
                                                                        color:
                                                                          "white",
                                                                        fontSize:
                                                                          "12px",
                                                                        padding:
                                                                          "2px 8px 2px 8px",
                                                                        borderRadius: 10,
                                                                      }}
                                                                    >
                                                                      {isReportSynced(
                                                                        report[
                                                                        "sync_status"
                                                                        ]
                                                                      )
                                                                        ? findTheDateOnWhichReportIsSynced(
                                                                          report[
                                                                          "sync_status"
                                                                          ]
                                                                        )
                                                                        : "N/A"}
                                                                    </p>
                                                                  </div>
                                                                  <div className="d-flex justify-content-between">
                                                                    <p>
                                                                      Sync Mode
                                                                    </p>
                                                                    <p
                                                                      style={{
                                                                        backgroundColor:
                                                                          findSyncModeAndSyncedTo(
                                                                            report[
                                                                            "sync_status"
                                                                            ]
                                                                          )[
                                                                            "syncMode"
                                                                          ] !=
                                                                            "N/A"
                                                                            ? "#348355"
                                                                            : "#DCAF0D",
                                                                        color:
                                                                          "white",
                                                                        fontSize:
                                                                          "12px",
                                                                        padding:
                                                                          "2px 8px 2px 8px",
                                                                        borderRadius: 10,
                                                                      }}
                                                                    >
                                                                      {
                                                                        findSyncModeAndSyncedTo(
                                                                          report[
                                                                          "sync_status"
                                                                          ]
                                                                        )[
                                                                        "syncMode"
                                                                        ]
                                                                      }
                                                                    </p>
                                                                  </div>
                                                                  <div className="d-flex justify-content-between">
                                                                    <p>
                                                                      Synced To
                                                                    </p>
                                                                    <p
                                                                      style={{
                                                                        backgroundColor:
                                                                          findSyncModeAndSyncedTo(
                                                                            report[
                                                                            "sync_status"
                                                                            ]
                                                                          )[
                                                                            "syncMode"
                                                                          ] !=
                                                                            "N/A"
                                                                            ? "#348355"
                                                                            : "#DCAF0D",
                                                                        color:
                                                                          "white",
                                                                        fontSize:
                                                                          "12px",
                                                                        padding:
                                                                          "2px 8px 2px 8px",
                                                                        borderRadius: 10,
                                                                      }}
                                                                    >
                                                                      {
                                                                        findSyncModeAndSyncedTo(
                                                                          report[
                                                                          "sync_status"
                                                                          ]
                                                                        )[
                                                                        "syncedTo"
                                                                        ]
                                                                      }
                                                                    </p>
                                                                  </div>
                                                                  <hr className="featurette-divider" />
                                                                  <div className="d-flex justify-content-between">
                                                                    <p>
                                                                      Grand
                                                                      Total
                                                                    </p>
                                                                    <p
                                                                      style={{
                                                                        backgroundColor:
                                                                          report[
                                                                            "grand_total"
                                                                          ] ===
                                                                            0
                                                                            ? "#348355"
                                                                            : "#DCAF0D",
                                                                        color:
                                                                          "white",
                                                                        fontSize:
                                                                          "12px",
                                                                        padding:
                                                                          "2px 8px 2px 8px",
                                                                        borderRadius: 10,
                                                                      }}
                                                                    >
                                                                      {
                                                                        report[
                                                                        "grand_total"
                                                                        ]
                                                                      }
                                                                      <span
                                                                        style={{
                                                                          cursor:
                                                                            "pointer",
                                                                        }}
                                                                        onClick={() => {
                                                                          const body =
                                                                          {
                                                                            restaurant_id:
                                                                              rest_id,
                                                                            report_date:
                                                                              date,
                                                                          };
                                                                          calculateAndUpdateGrandTotal(
                                                                            body,
                                                                            {
                                                                              onSuccess:
                                                                                () => {
                                                                                  queryClient.invalidateQueries({ queryKey: ["master-report-status", date] });
                                                                                },
                                                                            }
                                                                          );
                                                                        }}
                                                                      >
                                                                        <ArrowRepeat
                                                                          className={`mx-1 ${calculatingAndUpdateGrandTotal
                                                                              ? "spin-animation"
                                                                              : ""
                                                                            }`}
                                                                          style={{
                                                                            height: 15,
                                                                            width: 15,
                                                                          }}
                                                                        />
                                                                      </span>
                                                                    </p>
                                                                  </div>

                                                                  <hr className="featurette-divider" />
                                                                  <div className="d-flex justify-content-between">
                                                                    <p>
                                                                      Net Sales
                                                                    </p>
                                                                    <p
                                                                      style={{
                                                                        backgroundColor:
                                                                          report?.[
                                                                            "net_sales"
                                                                          ] > 0
                                                                            ? "#348355"
                                                                            : "#DCAF0D",
                                                                        color:
                                                                          "white",
                                                                        fontSize:
                                                                          "12px",
                                                                        padding:
                                                                          "2px 8px 2px 8px",
                                                                        borderRadius: 10,
                                                                      }}
                                                                    >
                                                                      {report?.[
                                                                        "net_sales"
                                                                      ] > 0
                                                                        ? report[
                                                                        "net_sales"
                                                                        ]
                                                                        : "No Sales"}
                                                                    </p>
                                                                  </div>

                                                                  <hr className="featurette-divider" />
                                                                  <span className="fw-bold">
                                                                    Scraper Jobs
                                                                  </span>
                                                                  {report?.[
                                                                    "scraper_jobs"
                                                                  ]?.map(
                                                                    (job) => {
                                                                      return (
                                                                        <Card>
                                                                          <Card.Header
                                                                            style={{
                                                                              textTransform:
                                                                                "capitalize",
                                                                              border:
                                                                                job.job_status ===
                                                                                  "in progress" ||
                                                                                  job.job_status.toLowerCase() ===
                                                                                  "scheduled"
                                                                                  ? "2px solid #dcaf0d"
                                                                                  : job.job_status ===
                                                                                    "stopped" ||
                                                                                    job.job_status ===
                                                                                    "Failed"
                                                                                    ? "2px solid #D14E4E"
                                                                                    : "2px solid #348355",
                                                                              borderCollapse:
                                                                                "collapse",
                                                                            }}
                                                                          >
                                                                            <div className="d-flex justify-content-between align-items-center">
                                                                              {
                                                                                job
                                                                                  ?.task_details
                                                                                  ?.schedule_type
                                                                              }{" "}
                                                                              -{" "}
                                                                              {
                                                                                job
                                                                                  ?.scheduler_details
                                                                                  ?.pos_name
                                                                              }
                                                                              <span>
                                                                                <TerminalFill
                                                                                  title="View Job Logs"
                                                                                  className="mx-2"
                                                                                  fill="gray"
                                                                                  style={{
                                                                                    cursor:
                                                                                      "pointer",
                                                                                  }}
                                                                                  onClick={() => {
                                                                                    setTaskIdForLogs(
                                                                                      job.task_id
                                                                                    );
                                                                                    setShowTaskLogsModal(
                                                                                      true
                                                                                    );
                                                                                    // navigate(
                                                                                    //   `/restaurants/${job.job_id}/scrapper-processes/job-logs/`
                                                                                    // );
                                                                                  }}
                                                                                />
                                                                                {job.job_status !==
                                                                                  "in progress" &&
                                                                                  new Date(
                                                                                    `${job.task_details?.execution_date} ${job.task_details?.execution_time}`
                                                                                  ) <
                                                                                  new Date()}
                                                                                <ArrowRepeat
                                                                                  title="Retry Job"
                                                                                  style={{
                                                                                    cursor:
                                                                                      "pointer",
                                                                                  }}
                                                                                  onClick={() => {
                                                                                    setTaskIdToStart(
                                                                                      job.task_id
                                                                                    );
                                                                                  }}
                                                                                />
                                                                                {job
                                                                                  ?.task_details
                                                                                  ?.screenshot
                                                                                  ?.length >
                                                                                  0 &&
                                                                                  job?.task_details.screenshot.every(
                                                                                    (
                                                                                      it
                                                                                    ) =>
                                                                                      it !==
                                                                                      null
                                                                                  ) && (
                                                                                    <EyeFill
                                                                                      title="View Screenshots"
                                                                                      className="mx-2"
                                                                                      fill="gray"
                                                                                      style={{
                                                                                        cursor:
                                                                                          "pointer",
                                                                                      }}
                                                                                      onClick={() => {
                                                                                        openScreenshots(job.task_id);
                                                                                      }}
                                                                                    />
                                                                                  )}
                                                                              </span>
                                                                            </div>
                                                                          </Card.Header>
                                                                          <Card.Body
                                                                            style={{
                                                                              padding: 7,
                                                                              border:
                                                                                job.job_status ===
                                                                                  "in progress" ||
                                                                                  job.job_status.toLowerCase() ===
                                                                                  "scheduled"
                                                                                  ? "2px solid #DCAF0D"
                                                                                  : job.job_status ===
                                                                                    "stopped" ||
                                                                                    job.job_status ===
                                                                                    "Failed"
                                                                                    ? "2px solid #D14E4E"
                                                                                    : "2px solid #348355",
                                                                              borderCollapse:
                                                                                "collapse",
                                                                            }}
                                                                          >
                                                                            <div className="d-flex justify-content-between align-items-center">
                                                                              <p className="mb-0">Job Status</p>
                                                                              <div className="d-flex align-items-center gap-2">
                                                                                {editingJobTaskId === job.task_id ? (
                                                                                  <>
                                                                                    <select
                                                                                      className="form-select form-select-sm"
                                                                                      style={{ fontSize: "12px", width: "130px" }}
                                                                                      value={selectedNewStatus}
                                                                                      onChange={(e) => setSelectedNewStatus(e.target.value)}
                                                                                    >
                                                                                      <option value="" disabled>Select status</option>
                                                                                      {["in progress", "scheduled", "completed", "failed", "stopped"].map((s) => (
                                                                                        <option key={s} value={s}>{s}</option>
                                                                                      ))}
                                                                                    </select>
                                                                                    <button
                                                                                      className="btn btn-success btn-sm py-0 px-2"
                                                                                      style={{ fontSize: "11px" }}
                                                                                      disabled={!selectedNewStatus || updatingJobStatus}
                                                                                      onClick={() => {
                                                                                        updateJobStatus(
                                                                                          { task_id: job.task_id, job_status: selectedNewStatus, report_date: date, restaurant_id: rest_id },
                                                                                          {
                                                                                            onSuccess: () => {
                                                                                              queryClient.invalidateQueries({ queryKey: ["master-report-status", date] });
                                                                                              setEditingJobTaskId(null);
                                                                                              setSelectedNewStatus("");
                                                                                            },
                                                                                          }
                                                                                        );
                                                                                      }}
                                                                                    >
                                                                                      {updatingJobStatus ? "..." : "Save"}
                                                                                    </button>
                                                                                    <button
                                                                                      className="btn btn-secondary btn-sm py-0 px-2"
                                                                                      style={{ fontSize: "11px" }}
                                                                                      onClick={() => {
                                                                                        setEditingJobTaskId(null);
                                                                                        setSelectedNewStatus("");
                                                                                      }}
                                                                                    >
                                                                                      ✕
                                                                                    </button>
                                                                                  </>
                                                                                ) : (
                                                                                  <>
                                                                                    <p className="mb-0" style={{ textTransform: "capitalize" }}>
                                                                                      {job["job_status"]}
                                                                                    </p>
                                                                                    <PencilFill
                                                                                      title="Edit Job Status"
                                                                                      size={11}
                                                                                      fill="gray"
                                                                                      style={{ cursor: "pointer" }}
                                                                                      onClick={() => {
                                                                                        setEditingJobTaskId(job.task_id);
                                                                                        setSelectedNewStatus(job["job_status"]);
                                                                                      }}
                                                                                    />
                                                                                  </>
                                                                                )}
                                                                              </div>
                                                                            </div>
                                                                            <div className="d-flex justify-content-between">
                                                                              <p>
                                                                                Start
                                                                                Time
                                                                              </p>
                                                                              <p
                                                                                style={{
                                                                                  textTransform:
                                                                                    "capitalize",
                                                                                }}
                                                                              >
                                                                                {
                                                                                  job[
                                                                                  "job_start_time"
                                                                                  ]
                                                                                }
                                                                              </p>
                                                                            </div>
                                                                            <div className="d-flex justify-content-between">
                                                                              <p>
                                                                                End
                                                                                Time
                                                                              </p>
                                                                              <p
                                                                                style={{
                                                                                  textTransform:
                                                                                    "capitalize",
                                                                                }}
                                                                              >
                                                                                {
                                                                                  job[
                                                                                  "job_end_time"
                                                                                  ]
                                                                                }
                                                                              </p>
                                                                            </div>
                                                                            <div className="d-flex justify-content-between">
                                                                              <p>
                                                                                Extraction
                                                                                Type
                                                                              </p>
                                                                              <p
                                                                                style={{
                                                                                  textTransform:
                                                                                    "capitalize",
                                                                                }}
                                                                              >
                                                                                {
                                                                                  job[
                                                                                  "task_details"
                                                                                  ]?.[
                                                                                  "extraction_type"
                                                                                  ]
                                                                                }
                                                                              </p>
                                                                            </div>
                                                                            <div className="d-flex justify-content-between">
                                                                              <p>
                                                                                Execution
                                                                                Date
                                                                              </p>
                                                                              <p
                                                                                style={{
                                                                                  textTransform:
                                                                                    "capitalize",
                                                                                }}
                                                                              >
                                                                                {
                                                                                  job[
                                                                                  "task_details"
                                                                                  ]?.[
                                                                                  "execution_date"
                                                                                  ]
                                                                                }
                                                                              </p>
                                                                            </div>
                                                                            <div className="d-flex justify-content-between">
                                                                              <p>
                                                                                Execution
                                                                                Time
                                                                              </p>
                                                                              <p
                                                                                style={{
                                                                                  textTransform:
                                                                                    "capitalize",
                                                                                }}
                                                                              >
                                                                                {
                                                                                  job[
                                                                                  "task_details"
                                                                                  ]?.[
                                                                                  "execution_time"
                                                                                  ]
                                                                                }
                                                                              </p>
                                                                            </div>
                                                                          </Card.Body>
                                                                        </Card>
                                                                      );
                                                                    }
                                                                  )}
                                                                </>
                                                              );
                                                            }
                                                          )}
                                                        </Accordion.Body>
                                                      </Accordion.Item>
                                                    </Accordion>
                                                  )}
                                              </div>
                                            );
                                          }
                                        )
                                      )}
                                    </div>
                                  </Accordion.Body>
                                </Accordion.Item>
                              </Accordion>
                            </div>
                          )}

                          {colAllWarningRestIds.length > 0 && (
                            <div className="text-sm-center px-1  ">
                              <Accordion className="my-2" defaultActiveKey={null} style={{ "--category-accent": "#DCAF0D" }}>
                                <Accordion.Item eventKey="1">
                                  <Accordion.Header className="custom-header-warning">
                                    <div>Warning Reports ({colWarningRestIds.length})</div>
                                  </Accordion.Header>
                                  <Accordion.Body
                                    mountOnEnter
                                    unmountOnExit
                                    style={{
                                      padding: 0,
                                      textAlign: "left",
                                    }}
                                  >
                                    <div>
                                      {/* Unbalanced Reports Subsection */}
                                      {colUnbalancedRestIds.length > 0 && (
                                        <Accordion className="my-2 mx-2" defaultActiveKey="1">
                                          <Accordion.Item eventKey="1">
                                            <Accordion.Header className="custom-header-warning">
                                              <div>Unbalanced Reports ({colUnbalancedRestIds.length})</div>
                                            </Accordion.Header>
                                            <Accordion.Body style={{ padding: 0, textAlign: "left" }}>
                                              <div>
                                                <div key={date}>
                                                  {data &&
                                                    colUnbalancedRestIds.map((rest_id, i) => (
                                                      <div className="mx-2" key={i}>
                                                        {renderRestaurantAccordion(rest_id, i, date)}
                                                      </div>
                                                    ))}
                                                </div>
                                              </div>
                                            </Accordion.Body>
                                          </Accordion.Item>
                                        </Accordion>
                                      )}

                                      {/* Unsynced Reports Subsection */}
                                      {colUnsyncedRestIds.length > 0 && (
                                        <Accordion className="my-2 mx-2" defaultActiveKey="1">
                                          <Accordion.Item eventKey="1">
                                            <Accordion.Header className="custom-header-warning">
                                              <div>Unsynced Reports ({colUnsyncedRestIds.length})</div>
                                            </Accordion.Header>
                                            <Accordion.Body style={{ padding: 0, textAlign: "left" }}>
                                              <div>
                                                <div key={date}>
                                                  {data &&
                                                    colUnsyncedRestIds.map((rest_id, i) => (
                                                      <div className="mx-2" key={i}>
                                                        {renderRestaurantAccordion(rest_id, i, date)}
                                                      </div>
                                                    ))}
                                                </div>
                                              </div>
                                            </Accordion.Body>
                                          </Accordion.Item>
                                        </Accordion>
                                      )}
                                    </div>
                                  </Accordion.Body>
                                </Accordion.Item>
                              </Accordion>
                            </div>
                          )}
                          {colSuccessfulRestIds.length > 0 && (
                            <div className="text-sm-center px-1  ">
                              <Accordion className="my-2" defaultActiveKey={null} style={{ "--category-accent": "#348355" }}>
                                <Accordion.Item eventKey="1">
                                  <Accordion.Header className="custom-header">
                                    <div>Successful Reports ({colSuccessfulRestIds.length})</div>
                                  </Accordion.Header>
                                  <Accordion.Body
                                    mountOnEnter
                                    unmountOnExit
                                    style={{
                                      padding: 0,
                                      textAlign: "left",
                                    }}
                                  >
                                    <div>
                                      <div key={date}>
                                        {data &&
                                          colSuccessfulRestIds.map(
                                            (rest_id, i) => {
                                              return (
                                                <div className="mx-2">
                                                  {data?.data[date][
                                                    rest_id
                                                  ]?.map(
                                                    (report, rindx) =>
                                                      !report[
                                                        "report_status"
                                                      ]?.some(
                                                        (it) =>
                                                          !it[
                                                            "report_status"
                                                          ].toLowerCase() ===
                                                          "failed"
                                                      )
                                                  ) &&
                                                    isReportSynced(
                                                      data?.data[date][
                                                        rest_id
                                                      ]?.[0]?.sync_status
                                                    ) &&
                                                    data?.data[date][
                                                      rest_id
                                                    ]?.some(
                                                      (report) =>
                                                        report?.["net_sales"] >
                                                        0
                                                    ) && (
                                                      <Accordion
                                                        defaultActiveKey={i}
                                                        className="my-2 h-75"
                                                      >
                                                        <Accordion.Item
                                                          eventKey="0"
                                                          style={{
                                                            border: {},
                                                          }}
                                                        >
                                                          <Accordion.Header
                                                            title={
                                                              data
                                                                .id_name_map?.[
                                                              rest_id
                                                              ]
                                                            }
                                                          >
                                                            <div
                                                              style={{
                                                                display: "flex",
                                                                justifyContent:
                                                                  "space-between",
                                                                width: "100%",
                                                                alignItems:
                                                                  "center",
                                                              }}
                                                            >
                                                              <span
                                                                style={{
                                                                  fontSize:
                                                                    "13px",
                                                                }}
                                                              // title={
                                                              //   data.id_name_map?.[rest_id]
                                                              // }
                                                              >
                                                                {data
                                                                  .id_name_map?.[
                                                                  rest_id
                                                                ].length > 17
                                                                  ? data.id_name_map?.[
                                                                    rest_id
                                                                  ]?.slice(
                                                                    0,
                                                                    17
                                                                  ) + "..."
                                                                  : data
                                                                    .id_name_map?.[
                                                                  rest_id
                                                                  ]}
                                                              </span>
                                                              <div
                                                                style={{
                                                                  display:
                                                                    "flex",
                                                                  alignItems:
                                                                    "center",
                                                                  marginRight:
                                                                    "10px",
                                                                }}
                                                              >
                                                                {data?.data[
                                                                  date
                                                                ][
                                                                  rest_id
                                                                ]?.some(
                                                                  (report) =>
                                                                    !isReportSynced(
                                                                      report[
                                                                      "sync_status"
                                                                      ]
                                                                    )
                                                                ) && (
                                                                    <CircleFill
                                                                      fill="#a15243"
                                                                      className="mx-1"
                                                                      height={10}
                                                                      width={10}
                                                                      title="Unsynced Reports"
                                                                    />
                                                                  )}
                                                                {data?.data[
                                                                  date
                                                                ][
                                                                  rest_id
                                                                ]?.some(
                                                                  (report) =>
                                                                    report[
                                                                    "grand_total"
                                                                    ] !== 0
                                                                ) && (
                                                                    <CircleFill
                                                                      fill="#413b47"
                                                                      className="mx-1"
                                                                      height={10}
                                                                      width={10}
                                                                      title="Grand Total Not Zero"
                                                                    />
                                                                  )}
                                                              </div>
                                                            </div>
                                                          </Accordion.Header>
                                                          <Accordion.Body
                                                            style={{
                                                              textAlign: "left",
                                                            }}
                                                          >
                                                            {data?.data[date][
                                                              rest_id
                                                            ]?.map(
                                                              (
                                                                report,
                                                                rindx
                                                              ) => {
                                                                return (
                                                                  <>
                                                                    {report[
                                                                      "report_status"
                                                                    ]?.filter(
                                                                      (it) =>
                                                                        it[
                                                                          "report_status"
                                                                        ]?.toLowerCase() !==
                                                                        "failed"
                                                                    ).length >
                                                                      0 && (
                                                                        <p className="fw-bold text-left">
                                                                          <Link
                                                                            className="text-body"
                                                                            style={{
                                                                              textDecoration:
                                                                                "underline",
                                                                            }}
                                                                            to={`/restaurants/${rest_id}/jhbadhbjasd/sales-receipt/?date=${date}&restaurant_name=${data.id_name_map?.[rest_id]}`}
                                                                            target="_blank"
                                                                          >
                                                                            Report
                                                                            Status
                                                                          </Link>
                                                                        </p>
                                                                      )}
                                                                    <div
                                                                      key={
                                                                        rindx
                                                                      }
                                                                    >
                                                                      {report[
                                                                        "report_status"
                                                                      ]
                                                                        ?.filter(
                                                                          (
                                                                            it
                                                                          ) =>
                                                                            it[
                                                                              "report_status"
                                                                            ]?.toLowerCase() !==
                                                                            "failed"
                                                                        )
                                                                        ?.map(
                                                                          (
                                                                            r,
                                                                            i
                                                                          ) => {
                                                                            const backgroundColor =
                                                                              findStatus(
                                                                                r[
                                                                                "report_status"
                                                                                ],
                                                                                r[
                                                                                "classified_flag"
                                                                                ],
                                                                                r[
                                                                                "report_name"
                                                                                ]
                                                                              ) ===
                                                                                "Completed"
                                                                                ? "#348355"
                                                                                : findStatus(
                                                                                  r[
                                                                                  "report_status"
                                                                                  ],
                                                                                  r[
                                                                                  "classified_flag"
                                                                                  ],
                                                                                  r[
                                                                                  "report_name"
                                                                                  ]
                                                                                ) ===
                                                                                  "Downloaded"
                                                                                  ? "linear-gradient(to right, #348355 50%, #DCAF0D 50%)"
                                                                                  : findStatus(
                                                                                    r[
                                                                                    "report_status"
                                                                                    ],
                                                                                    r[
                                                                                    "classified_flag"
                                                                                    ],
                                                                                    r[
                                                                                    "report_name"
                                                                                    ]
                                                                                  ) ===
                                                                                    "Failed"
                                                                                    ? "#D14E4E"
                                                                                    : "#DCAF0D";
                                                                            const style =
                                                                            {
                                                                              background:
                                                                                backgroundColor,
                                                                              color:
                                                                                "white",
                                                                              fontSize:
                                                                                "12px",
                                                                              padding:
                                                                                "2px 8px 2px 8px",
                                                                              borderRadius: 10,
                                                                            };
                                                                            return (
                                                                              <div
                                                                                key={
                                                                                  i
                                                                                }
                                                                                style={{
                                                                                  display:
                                                                                    "flex",
                                                                                  flexDirection:
                                                                                    "row",
                                                                                  justifyContent:
                                                                                    "space-between",
                                                                                }}
                                                                              >
                                                                                <p>
                                                                                  {
                                                                                    r[
                                                                                    "report_name"
                                                                                    ]
                                                                                  }
                                                                                </p>
                                                                                <p
                                                                                  style={
                                                                                    style
                                                                                  }
                                                                                  title={
                                                                                    findStatus(
                                                                                      r[
                                                                                      "report_status"
                                                                                      ],
                                                                                      r[
                                                                                      "classified_flag"
                                                                                      ],
                                                                                      r[
                                                                                      "report_name"
                                                                                      ]
                                                                                    ) ===
                                                                                      "Downloaded"
                                                                                      ? "Classification Pending"
                                                                                      : findStatus(
                                                                                        r[
                                                                                        "report_status"
                                                                                        ],
                                                                                        r[
                                                                                        "classified_flag"
                                                                                        ],
                                                                                        r[
                                                                                        "report_name"
                                                                                        ]
                                                                                      )
                                                                                  }
                                                                                >
                                                                                  {findStatus(
                                                                                    r[
                                                                                    "report_status"
                                                                                    ],
                                                                                    r[
                                                                                    "classified_flag"
                                                                                    ],
                                                                                    r[
                                                                                    "report_name"
                                                                                    ]
                                                                                  )}
                                                                                </p>
                                                                              </div>
                                                                            );
                                                                          }
                                                                        )}
                                                                    </div>
                                                                    {report[
                                                                      "report_status"
                                                                    ]?.filter(
                                                                      (it) =>
                                                                        it[
                                                                          "report_status"
                                                                        ]?.toLowerCase() !==
                                                                        "failed"
                                                                    ).length >
                                                                      0 && (
                                                                        <hr className="featurette-divider my-2" />
                                                                      )}
                                                                    <p className="fw-bold">
                                                                      {" "}
                                                                      Sync
                                                                      Status
                                                                    </p>
                                                                    <div className="d-flex justify-content-between">
                                                                      <p>
                                                                        Sync
                                                                        Status
                                                                      </p>
                                                                      <p
                                                                        style={{
                                                                          backgroundColor:
                                                                            isReportSynced(
                                                                              report[
                                                                              "sync_status"
                                                                              ]
                                                                            )
                                                                              ? "#348355"
                                                                              : "#D14E4E",
                                                                          color:
                                                                            "white",
                                                                          fontSize:
                                                                            "12px",
                                                                          padding:
                                                                            "2px 8px 2px 8px",
                                                                          borderRadius: 10,
                                                                          cursor: "pointer"
                                                                        }}
                                                                        onClick={() => {
                                                                          let qboLogs = report["sync_status"]["qbo_sync"];
                                                                          let clickbaconLogs = report["sync_status"]["clickbacon_sync"];
                                                                          let logs = qboLogs.concat(clickbaconLogs);
                                                                          if (logs.length > 0) {
                                                                            setShowSyncLogsModal(true);
                                                                            setSyncLogs(logs);

                                                                          }
                                                                        }}
                                                                      >
                                                                        {isReportSynced(
                                                                          report[
                                                                          "sync_status"
                                                                          ]
                                                                        )
                                                                          ? "Synced"
                                                                          : "Pending"}
                                                                      </p>
                                                                    </div>
                                                                    <div className="d-flex justify-content-between">
                                                                      <p>
                                                                        Synced
                                                                        At
                                                                      </p>
                                                                      <p
                                                                        style={{
                                                                          backgroundColor:
                                                                            isReportSynced(
                                                                              report[
                                                                              "sync_status"
                                                                              ]
                                                                            )
                                                                              ? "#348355"
                                                                              : "#D14E4E",
                                                                          color:
                                                                            "white",
                                                                          fontSize:
                                                                            "12px",
                                                                          padding:
                                                                            "2px 8px 2px 8px",
                                                                          borderRadius: 10,
                                                                        }}
                                                                      >
                                                                        {isReportSynced(
                                                                          report[
                                                                          "sync_status"
                                                                          ]
                                                                        )
                                                                          ? findTheDateOnWhichReportIsSynced(
                                                                            report[
                                                                            "sync_status"
                                                                            ]
                                                                          )
                                                                          : "N/A"}
                                                                      </p>
                                                                    </div>
                                                                    <div className="d-flex justify-content-between">
                                                                      <p>
                                                                        Sync
                                                                        Mode
                                                                      </p>
                                                                      <p
                                                                        style={{
                                                                          backgroundColor:
                                                                            findSyncModeAndSyncedTo(
                                                                              report[
                                                                              "sync_status"
                                                                              ]
                                                                            )[
                                                                              "syncMode"
                                                                            ] !=
                                                                              "N/A"
                                                                              ? "#348355"
                                                                              : "#D14E4E",
                                                                          color:
                                                                            "white",
                                                                          fontSize:
                                                                            "12px",
                                                                          padding:
                                                                            "2px 8px 2px 8px",
                                                                          borderRadius: 10,
                                                                        }}
                                                                      >
                                                                        {
                                                                          findSyncModeAndSyncedTo(
                                                                            report[
                                                                            "sync_status"
                                                                            ]
                                                                          )[
                                                                          "syncMode"
                                                                          ]
                                                                        }
                                                                      </p>
                                                                    </div>
                                                                    <div className="d-flex justify-content-between">
                                                                      <p>
                                                                        Synced
                                                                        To
                                                                      </p>
                                                                      <p
                                                                        style={{
                                                                          backgroundColor:
                                                                            findSyncModeAndSyncedTo(
                                                                              report[
                                                                              "sync_status"
                                                                              ]
                                                                            )[
                                                                              "syncMode"
                                                                            ] !=
                                                                              "N/A"
                                                                              ? "#348355"
                                                                              : "#D14E4E",
                                                                          color:
                                                                            "white",
                                                                          fontSize:
                                                                            "12px",
                                                                          padding:
                                                                            "2px 8px 2px 8px",
                                                                          borderRadius: 10,
                                                                        }}
                                                                      >
                                                                        {
                                                                          findSyncModeAndSyncedTo(
                                                                            report[
                                                                            "sync_status"
                                                                            ]
                                                                          )[
                                                                          "syncedTo"
                                                                          ]
                                                                        }
                                                                      </p>
                                                                    </div>
                                                                    <hr className="featurette-divider" />
                                                                    <div className="d-flex justify-content-between">
                                                                      <p>
                                                                        Grand
                                                                        Total
                                                                      </p>
                                                                      <p
                                                                        style={{
                                                                          backgroundColor:
                                                                            report[
                                                                              "grand_total"
                                                                            ] ===
                                                                              0
                                                                              ? "#348355"
                                                                              : "#D14E4E",
                                                                          color:
                                                                            "white",
                                                                          fontSize:
                                                                            "12px",
                                                                          padding:
                                                                            "2px 8px 2px 8px",
                                                                          borderRadius: 10,
                                                                        }}
                                                                      >
                                                                        {
                                                                          report[
                                                                          "grand_total"
                                                                          ]
                                                                        }
                                                                        <span
                                                                          style={{
                                                                            cursor:
                                                                              "pointer",
                                                                          }}
                                                                          onClick={() => {
                                                                            const body =
                                                                            {
                                                                              restaurant_id:
                                                                                rest_id,
                                                                              report_date:
                                                                                date,
                                                                            };
                                                                            calculateAndUpdateGrandTotal(
                                                                              body,
                                                                              {
                                                                                onSuccess:
                                                                                  () => {
                                                                                    queryClient.invalidateQueries({ queryKey: ["master-report-status", date] });
                                                                                  },
                                                                              }
                                                                            );
                                                                          }}
                                                                        >
                                                                          <ArrowRepeat
                                                                            className={`mx-1 ${calculatingAndUpdateGrandTotal
                                                                                ? "spin-animation"
                                                                                : ""
                                                                              }`}
                                                                            style={{
                                                                              height: 15,
                                                                              width: 15,
                                                                            }}
                                                                          />
                                                                        </span>
                                                                      </p>
                                                                    </div>

                                                                    <hr className="featurette-divider" />
                                                                    <div className="d-flex justify-content-between">
                                                                      <p>
                                                                        Net
                                                                        Sales
                                                                      </p>
                                                                      <p
                                                                        style={{
                                                                          backgroundColor:
                                                                            report?.[
                                                                              "net_sales"
                                                                            ] >
                                                                              0
                                                                              ? "#348355"
                                                                              : "#DCAF0D",
                                                                          color:
                                                                            "white",
                                                                          fontSize:
                                                                            "12px",
                                                                          padding:
                                                                            "2px 8px 2px 8px",
                                                                          borderRadius: 10,
                                                                        }}
                                                                      >
                                                                        {report?.[
                                                                          "net_sales"
                                                                        ] > 0
                                                                          ? report?.[
                                                                          "net_sales"
                                                                          ]
                                                                          : "No Sales"}
                                                                      </p>
                                                                    </div>

                                                                    <hr className="featurette-divider" />
                                                                    <span className="fw-bold">
                                                                      Scraper
                                                                      Jobs
                                                                    </span>
                                                                    {report?.[
                                                                      "scraper_jobs"
                                                                    ]?.map(
                                                                      (job) => {
                                                                        return (
                                                                          <Card>
                                                                            <Card.Header
                                                                              style={{
                                                                                textTransform:
                                                                                  "capitalize",
                                                                                border:
                                                                                  job.job_status ===
                                                                                    "in progress" ||
                                                                                    job.job_status.toLowerCase() ===
                                                                                    "scheduled"
                                                                                    ? "2px solid #DCAF0D"
                                                                                    : job.job_status ===
                                                                                      "stopped"
                                                                                      ? "2px solid #D14E4E"
                                                                                      : "2px solid #348355",
                                                                                borderCollapse:
                                                                                  "collapse",
                                                                              }}
                                                                            >
                                                                              <div className="d-flex justify-content-between align-items-center">
                                                                                {
                                                                                  job
                                                                                    ?.task_details
                                                                                    ?.schedule_type
                                                                                }{" "}
                                                                                -{" "}
                                                                                {
                                                                                  job
                                                                                    ?.scheduler_details
                                                                                    ?.pos_name
                                                                                }
                                                                                <span>
                                                                                  <TerminalFill
                                                                                    title="View Job Logs"
                                                                                    className="mx-2"
                                                                                    fill="gray"
                                                                                    style={{
                                                                                      cursor:
                                                                                        "pointer",
                                                                                    }}
                                                                                    onClick={() => {
                                                                                      setTaskIdForLogs(
                                                                                        job.task_id
                                                                                      );
                                                                                      setShowTaskLogsModal(
                                                                                        true
                                                                                      );
                                                                                      // navigate(
                                                                                      //   `/restaurants/${job.job_id}/scrapper-processes/job-logs/`
                                                                                      // );
                                                                                    }}
                                                                                  />
                                                                                  {job.job_status !==
                                                                                    "in progress" &&
                                                                                    new Date(
                                                                                      `${job.task_details?.execution_date} ${job.task_details?.execution_time}`
                                                                                    ) <
                                                                                    new Date()}
                                                                                  <ArrowRepeat
                                                                                    title="Retry Job"
                                                                                    style={{
                                                                                      cursor:
                                                                                        "pointer",
                                                                                    }}
                                                                                    onClick={() => {
                                                                                      setTaskIdToStart(
                                                                                        job.task_id
                                                                                      );
                                                                                    }}
                                                                                  />
                                                                                </span>
                                                                              </div>
                                                                            </Card.Header>
                                                                            <Card.Body
                                                                              style={{
                                                                                padding: 7,
                                                                                border:
                                                                                  job.job_status ===
                                                                                    "in progress" ||
                                                                                    job.job_status.toLowerCase() ===
                                                                                    "scheduled"
                                                                                    ? "2px solid #DCAF0D"
                                                                                    : job.job_status ===
                                                                                      "stopped"
                                                                                      ? "2px solid #D14E4E"
                                                                                      : "2px solid #348355",
                                                                                borderCollapse:
                                                                                  "collapse",
                                                                              }}
                                                                            >
                                                                              <div className="d-flex justify-content-between align-items-center">
                                                                                <p className="mb-0">Job Status</p>
                                                                                <div className="d-flex align-items-center gap-2">
                                                                                  {editingJobTaskId === job.task_id ? (
                                                                                    <>
                                                                                      <select
                                                                                        className="form-select form-select-sm"
                                                                                        style={{ fontSize: "12px", width: "130px" }}
                                                                                        value={selectedNewStatus}
                                                                                        onChange={(e) => setSelectedNewStatus(e.target.value)}
                                                                                      >
                                                                                        <option value="" disabled>Select status</option>
                                                                                        {["in progress", "scheduled", "completed", "failed", "stopped"].map((s) => (
                                                                                          <option key={s} value={s}>{s}</option>
                                                                                        ))}
                                                                                      </select>
                                                                                      <button
                                                                                        className="btn btn-success btn-sm py-0 px-2"
                                                                                        style={{ fontSize: "11px" }}
                                                                                        disabled={!selectedNewStatus || updatingJobStatus}
                                                                                        onClick={() => {
                                                                                          updateJobStatus(
                                                                                            { task_id: job.task_id, job_status: selectedNewStatus, report_date: date, restaurant_id: rest_id },
                                                                                            {
                                                                                              onSuccess: () => {
                                                                                                queryClient.invalidateQueries({ queryKey: ["master-report-status", date] });
                                                                                                setEditingJobTaskId(null);
                                                                                                setSelectedNewStatus("");
                                                                                              },
                                                                                            }
                                                                                          );
                                                                                        }}
                                                                                      >
                                                                                        {updatingJobStatus ? "..." : "Save"}
                                                                                      </button>
                                                                                      <button
                                                                                        className="btn btn-secondary btn-sm py-0 px-2"
                                                                                        style={{ fontSize: "11px" }}
                                                                                        onClick={() => {
                                                                                          setEditingJobTaskId(null);
                                                                                          setSelectedNewStatus("");
                                                                                        }}
                                                                                      >
                                                                                        ✕
                                                                                      </button>
                                                                                    </>
                                                                                  ) : (
                                                                                    <>
                                                                                      <p className="mb-0" style={{ textTransform: "capitalize" }}>
                                                                                        {job["job_status"]}
                                                                                      </p>
                                                                                      <PencilFill
                                                                                        title="Edit Job Status"
                                                                                        size={11}
                                                                                        fill="gray"
                                                                                        style={{ cursor: "pointer" }}
                                                                                        onClick={() => {
                                                                                          setEditingJobTaskId(job.task_id);
                                                                                          setSelectedNewStatus(job["job_status"]);
                                                                                        }}
                                                                                      />
                                                                                    </>
                                                                                  )}
                                                                                </div>
                                                                              </div>
                                                                              <div className="d-flex justify-content-between">
                                                                                <p>
                                                                                  Start
                                                                                  Time
                                                                                </p>
                                                                                <p
                                                                                  style={{
                                                                                    textTransform:
                                                                                      "capitalize",
                                                                                  }}
                                                                                >
                                                                                  {
                                                                                    job[
                                                                                    "job_start_time"
                                                                                    ]
                                                                                  }
                                                                                </p>
                                                                              </div>
                                                                              <div className="d-flex justify-content-between">
                                                                                <p>
                                                                                  End
                                                                                  Time
                                                                                </p>
                                                                                <p
                                                                                  style={{
                                                                                    textTransform:
                                                                                      "capitalize",
                                                                                  }}
                                                                                >
                                                                                  {
                                                                                    job[
                                                                                    "job_end_time"
                                                                                    ]
                                                                                  }
                                                                                </p>
                                                                              </div>
                                                                              <div className="d-flex justify-content-between">
                                                                                <p>
                                                                                  Extraction
                                                                                  Type
                                                                                </p>
                                                                                <p
                                                                                  style={{
                                                                                    textTransform:
                                                                                      "capitalize",
                                                                                  }}
                                                                                >
                                                                                  {
                                                                                    job[
                                                                                    "task_details"
                                                                                    ]?.[
                                                                                    "extraction_type"
                                                                                    ]
                                                                                  }
                                                                                </p>
                                                                              </div>
                                                                              <div className="d-flex justify-content-between">
                                                                                <p>
                                                                                  Execution
                                                                                  Date
                                                                                </p>
                                                                                <p
                                                                                  style={{
                                                                                    textTransform:
                                                                                      "capitalize",
                                                                                  }}
                                                                                >
                                                                                  {
                                                                                    job[
                                                                                    "task_details"
                                                                                    ]?.[
                                                                                    "execution_date"
                                                                                    ]
                                                                                  }
                                                                                </p>
                                                                              </div>
                                                                              <div className="d-flex justify-content-between">
                                                                                <p>
                                                                                  Execution
                                                                                  Time
                                                                                </p>
                                                                                <p
                                                                                  style={{
                                                                                    textTransform:
                                                                                      "capitalize",
                                                                                  }}
                                                                                >
                                                                                  {
                                                                                    job[
                                                                                    "task_details"
                                                                                    ]?.[
                                                                                    "execution_time"
                                                                                    ]
                                                                                  }
                                                                                </p>
                                                                              </div>
                                                                            </Card.Body>
                                                                          </Card>
                                                                        );
                                                                      }
                                                                    )}
                                                                  </>
                                                                );
                                                              }
                                                            )}
                                                          </Accordion.Body>
                                                        </Accordion.Item>
                                                      </Accordion>
                                                    )}
                                                </div>
                                              );
                                            }
                                          )}
                                      </div>
                                    </div>
                                  </Accordion.Body>
                                </Accordion.Item>
                              </Accordion>
                            </div>
                          )}

                          {colNoSalesRestIds.length > 0 && (
                            <div className="text-sm-center px-1  ">
                              <Accordion className="my-2" defaultActiveKey={null} style={{ "--category-accent": "#0d9edc" }}>
                                <Accordion.Item eventKey="1">
                                  <Accordion.Header className="custom-header-no-sales">
                                    <div>No Sales ({colNoSalesRestIds.length})</div>
                                  </Accordion.Header>
                                  <Accordion.Body
                                    mountOnEnter
                                    unmountOnExit
                                    style={{
                                      padding: 0,
                                      textAlign: "left",
                                    }}
                                  >
                                    <div>
                                      <div key={date}>
                                        {data &&
                                          colNoSalesRestIds.map(
                                            (rest_id, i) => {
                                              const hasNetSalesZeroOrNoNetSales =
                                                data?.data[date][rest_id]?.every(
                                                  (report) => (
                                                    report["net_sales"] === 0 ||
                                                    !report["net_sales"]) &&
                                                    report["scraper_jobs"].every(
                                                      (job) =>
                                                        job["job_status"].toLowerCase() ==
                                                        "completed"
                                                    )
                                                );

                                              return (
                                                <div className="mx-2">
                                                  {(false ||
                                                    hasNetSalesZeroOrNoNetSales) && (
                                                      <Accordion
                                                        defaultActiveKey={i}
                                                        className="my-2 h-75"
                                                      >
                                                        <Accordion.Item
                                                          eventKey="0"
                                                          style={{
                                                            border: {},
                                                          }}
                                                        >
                                                          <Accordion.Header
                                                            title={
                                                              data.id_name_map?.[
                                                              rest_id
                                                              ]
                                                            }
                                                          >
                                                            <div
                                                              style={{
                                                                display: "flex",
                                                                justifyContent:
                                                                  "space-between",
                                                                width: "100%",
                                                                alignItems:
                                                                  "center",
                                                              }}
                                                            >
                                                              <span
                                                                style={{
                                                                  fontSize:
                                                                    "13px",
                                                                }}
                                                              // title={
                                                              //   data.id_name_map?.[rest_id]
                                                              // }
                                                              >
                                                                {data
                                                                  .id_name_map?.[
                                                                  rest_id
                                                                ].length > 17
                                                                  ? data.id_name_map?.[
                                                                    rest_id
                                                                  ]?.slice(
                                                                    0,
                                                                    17
                                                                  ) + "..."
                                                                  : data
                                                                    .id_name_map?.[
                                                                  rest_id
                                                                  ]}
                                                              </span>
                                                              <div
                                                                style={{
                                                                  display: "flex",
                                                                  alignItems:
                                                                    "center",
                                                                  marginRight:
                                                                    "10px",
                                                                }}
                                                              >
                                                                {data?.data[date][
                                                                  rest_id
                                                                ]?.some(
                                                                  (report) =>
                                                                    !isReportSynced(
                                                                      report[
                                                                      "sync_status"
                                                                      ]
                                                                    )
                                                                ) && (
                                                                    <CircleFill
                                                                      fill="#a15243"
                                                                      className="mx-1"
                                                                      height={10}
                                                                      width={10}
                                                                      title="Unsynced Reports"
                                                                    />
                                                                  )}
                                                                {data?.data[date][
                                                                  rest_id
                                                                ]?.some(
                                                                  (report) =>
                                                                    report[
                                                                    "grand_total"
                                                                    ] !== 0
                                                                ) && (
                                                                    <CircleFill
                                                                      fill="#413b47"
                                                                      className="mx-1"
                                                                      height={10}
                                                                      width={10}
                                                                      title="Grand Total Not Zero"
                                                                    />
                                                                  )}
                                                              </div>
                                                            </div>
                                                          </Accordion.Header>
                                                          <Accordion.Body
                                                            style={{
                                                              textAlign: "left",
                                                            }}
                                                          >
                                                            {data?.data[date][
                                                              rest_id
                                                            ]?.map(
                                                              (report, rindx) => {
                                                                return (
                                                                  <>
                                                                    {report[
                                                                      "report_status"
                                                                    ]?.filter(
                                                                      (it) =>
                                                                        it[
                                                                          "report_status"
                                                                        ]?.toLowerCase() !==
                                                                        "failed" ||
                                                                        it[
                                                                          "report_status"
                                                                        ]?.toLowerCase() ==
                                                                        "failed"
                                                                    ).length >
                                                                      0 && (
                                                                        <p className="fw-bold">
                                                                          <Link
                                                                            className="text-body"
                                                                            style={{
                                                                              textDecoration:
                                                                                "underline",
                                                                            }}
                                                                            to={`/restaurants/${rest_id}/jhbadhbjasd/sales-receipt/?date=${date}&restaurant_name=${data.id_name_map?.[rest_id]}`}
                                                                            target="_blank"
                                                                          >
                                                                            Report
                                                                            Status
                                                                          </Link>
                                                                        </p>
                                                                      )}
                                                                    <div
                                                                      key={rindx}
                                                                    >
                                                                      {report[
                                                                        "report_status"
                                                                      ]?.map(
                                                                        (
                                                                          r,
                                                                          i
                                                                        ) => {
                                                                          const backgroundColor =
                                                                            findStatus(
                                                                              r[
                                                                              "report_status"
                                                                              ],
                                                                              r[
                                                                              "classified_flag"
                                                                              ],
                                                                              r[
                                                                              "report_name"
                                                                              ]
                                                                            ) ===
                                                                              "Completed"
                                                                              ? "#348355"
                                                                              : findStatus(
                                                                                r[
                                                                                "report_status"
                                                                                ],
                                                                                r[
                                                                                "classified_flag"
                                                                                ],
                                                                                r[
                                                                                "report_name"
                                                                                ]
                                                                              ) ===
                                                                                "Downloaded"
                                                                                ? "linear-gradient(to right, #348355 50%, #DCAF0D 50%)"
                                                                                : findStatus(
                                                                                  r[
                                                                                  "report_status"
                                                                                  ],
                                                                                  r[
                                                                                  "classified_flag"
                                                                                  ],
                                                                                  r[
                                                                                  "report_name"
                                                                                  ]
                                                                                ) ===
                                                                                  "Failed"
                                                                                  ? "#D14E4E"
                                                                                  : "#DCAF0D";

                                                                          const style =
                                                                          {
                                                                            background:
                                                                              backgroundColor,
                                                                            color:
                                                                              "white",
                                                                            fontSize:
                                                                              "12px",
                                                                            padding:
                                                                              "2px 8px 2px 8px",
                                                                            borderRadius: 10,
                                                                          };
                                                                          return (
                                                                            <div
                                                                              key={
                                                                                i
                                                                              }
                                                                              style={{
                                                                                display:
                                                                                  "flex",
                                                                                flexDirection:
                                                                                  "row",
                                                                                justifyContent:
                                                                                  "space-between",
                                                                              }}
                                                                            >
                                                                              <p>
                                                                                {
                                                                                  r[
                                                                                  "report_name"
                                                                                  ]
                                                                                }
                                                                              </p>
                                                                              <p
                                                                                style={
                                                                                  style
                                                                                }
                                                                                title={
                                                                                  findStatus(
                                                                                    r[
                                                                                    "report_status"
                                                                                    ],
                                                                                    r[
                                                                                    "classified_flag"
                                                                                    ],
                                                                                    r[
                                                                                    "report_name"
                                                                                    ]
                                                                                  ) ===
                                                                                    "Downloaded"
                                                                                    ? "Classification Pending"
                                                                                    : findStatus(
                                                                                      r[
                                                                                      "report_status"
                                                                                      ],
                                                                                      r[
                                                                                      "classified_flag"
                                                                                      ],
                                                                                      r[
                                                                                      "report_name"
                                                                                      ]
                                                                                    )
                                                                                }
                                                                              >
                                                                                {findStatus(
                                                                                  r[
                                                                                  "report_status"
                                                                                  ],
                                                                                  r[
                                                                                  "classified_flag"
                                                                                  ],
                                                                                  r[
                                                                                  "report_name"
                                                                                  ]
                                                                                )}
                                                                              </p>
                                                                            </div>
                                                                          );
                                                                        }
                                                                      )}
                                                                    </div>
                                                                    {report[
                                                                      "report_status"
                                                                    ]?.filter(
                                                                      (it) =>
                                                                        it[
                                                                          "report_status"
                                                                        ]?.toLowerCase() !==
                                                                        "failed"
                                                                    ).length >
                                                                      0 && (
                                                                        <hr className="featurette-divider my-2" />
                                                                      )}
                                                                    <p className="fw-bold">
                                                                      {" "}
                                                                      Sync Status
                                                                    </p>
                                                                    <div className="d-flex justify-content-between">
                                                                      <p>
                                                                        Sync
                                                                        Status
                                                                      </p>
                                                                      <p
                                                                        style={{
                                                                          backgroundColor:
                                                                            isReportSynced(
                                                                              report[
                                                                              "sync_status"
                                                                              ]
                                                                            )
                                                                              ? "#348355"
                                                                              : "#DCAF0D",
                                                                          color:
                                                                            "white",
                                                                          fontSize:
                                                                            "12px",
                                                                          padding:
                                                                            "2px 8px 2px 8px",
                                                                          borderRadius: 10,
                                                                          cursor: "pointer",
                                                                        }}

                                                                        onClick={() => {
                                                                          let qboLogs = report["sync_status"]["qbo_sync"];
                                                                          let clickbaconLogs = report["sync_status"]["clickbacon_sync"];
                                                                          let logs = qboLogs.concat(clickbaconLogs);
                                                                          if (logs.length > 0) {
                                                                            setShowSyncLogsModal(true);
                                                                            setSyncLogs(logs);

                                                                          }
                                                                        }}
                                                                      >
                                                                        {isReportSynced(
                                                                          report[
                                                                          "sync_status"
                                                                          ]
                                                                        )
                                                                          ? "Synced"
                                                                          : "Pending"}
                                                                      </p>
                                                                    </div>
                                                                    <div className="d-flex justify-content-between">
                                                                      <p>
                                                                        Synced At
                                                                      </p>
                                                                      <p
                                                                        style={{
                                                                          backgroundColor:
                                                                            isReportSynced(
                                                                              report[
                                                                              "sync_status"
                                                                              ]
                                                                            )
                                                                              ? "#348355"
                                                                              : "#DCAF0D",
                                                                          color:
                                                                            "white",
                                                                          fontSize:
                                                                            "12px",
                                                                          padding:
                                                                            "2px 8px 2px 8px",
                                                                          borderRadius: 10,
                                                                        }}
                                                                      >
                                                                        {isReportSynced(
                                                                          report[
                                                                          "sync_status"
                                                                          ]
                                                                        )
                                                                          ? findTheDateOnWhichReportIsSynced(
                                                                            report[
                                                                            "sync_status"
                                                                            ]
                                                                          )
                                                                          : "N/A"}
                                                                      </p>
                                                                    </div>
                                                                    <div className="d-flex justify-content-between">
                                                                      <p>
                                                                        Sync Mode
                                                                      </p>
                                                                      <p
                                                                        style={{
                                                                          backgroundColor:
                                                                            findSyncModeAndSyncedTo(
                                                                              report[
                                                                              "sync_status"
                                                                              ]
                                                                            )[
                                                                              "syncMode"
                                                                            ] !=
                                                                              "N/A"
                                                                              ? "#348355"
                                                                              : "#DCAF0D",
                                                                          color:
                                                                            "white",
                                                                          fontSize:
                                                                            "12px",
                                                                          padding:
                                                                            "2px 8px 2px 8px",
                                                                          borderRadius: 10,
                                                                        }}
                                                                      >
                                                                        {
                                                                          findSyncModeAndSyncedTo(
                                                                            report[
                                                                            "sync_status"
                                                                            ]
                                                                          )[
                                                                          "syncMode"
                                                                          ]
                                                                        }
                                                                      </p>
                                                                    </div>
                                                                    <div className="d-flex justify-content-between">
                                                                      <p>
                                                                        Synced To
                                                                      </p>
                                                                      <p
                                                                        style={{
                                                                          backgroundColor:
                                                                            findSyncModeAndSyncedTo(
                                                                              report[
                                                                              "sync_status"
                                                                              ]
                                                                            )[
                                                                              "syncMode"
                                                                            ] !=
                                                                              "N/A"
                                                                              ? "#348355"
                                                                              : "#DCAF0D",
                                                                          color:
                                                                            "white",
                                                                          fontSize:
                                                                            "12px",
                                                                          padding:
                                                                            "2px 8px 2px 8px",
                                                                          borderRadius: 10,
                                                                        }}
                                                                      >
                                                                        {
                                                                          findSyncModeAndSyncedTo(
                                                                            report[
                                                                            "sync_status"
                                                                            ]
                                                                          )[
                                                                          "syncedTo"
                                                                          ]
                                                                        }
                                                                      </p>
                                                                    </div>
                                                                    <hr className="featurette-divider" />
                                                                    <div className="d-flex justify-content-between">
                                                                      <p>
                                                                        Grand
                                                                        Total
                                                                      </p>
                                                                      <p
                                                                        style={{
                                                                          backgroundColor:
                                                                            report[
                                                                              "grand_total"
                                                                            ] ===
                                                                              0
                                                                              ? "#348355"
                                                                              : "#DCAF0D",
                                                                          color:
                                                                            "white",
                                                                          fontSize:
                                                                            "12px",
                                                                          padding:
                                                                            "2px 8px 2px 8px",
                                                                          borderRadius: 10,
                                                                        }}
                                                                      >
                                                                        {
                                                                          report[
                                                                          "grand_total"
                                                                          ]
                                                                        }
                                                                        <span
                                                                          style={{
                                                                            cursor:
                                                                              "pointer",
                                                                          }}
                                                                          onClick={() => {
                                                                            const body =
                                                                            {
                                                                              restaurant_id:
                                                                                rest_id,
                                                                              report_date:
                                                                                date,
                                                                            };
                                                                            calculateAndUpdateGrandTotal(
                                                                              body,
                                                                              {
                                                                                onSuccess:
                                                                                  () => {
                                                                                    queryClient.invalidateQueries({ queryKey: ["master-report-status", date] });
                                                                                  },
                                                                              }
                                                                            );
                                                                          }}
                                                                        >
                                                                          <ArrowRepeat
                                                                            className={`mx-1 ${calculatingAndUpdateGrandTotal
                                                                                ? "spin-animation"
                                                                                : ""
                                                                              }`}
                                                                            style={{
                                                                              height: 15,
                                                                              width: 15,
                                                                            }}
                                                                          />
                                                                        </span>
                                                                      </p>
                                                                    </div>

                                                                    <hr className="featurette-divider" />
                                                                    <div className="d-flex justify-content-between">
                                                                      <p>
                                                                        Net Sales
                                                                      </p>
                                                                      <p
                                                                        style={{
                                                                          backgroundColor:
                                                                            report?.[
                                                                              "net_sales"
                                                                            ] > 0
                                                                              ? "#348355"
                                                                              : "#DCAF0D",
                                                                          color:
                                                                            "white",
                                                                          fontSize:
                                                                            "12px",
                                                                          padding:
                                                                            "2px 8px 2px 8px",
                                                                          borderRadius: 10,
                                                                        }}
                                                                      >
                                                                        {report?.[
                                                                          "net_sales"
                                                                        ] > 0
                                                                          ? report[
                                                                          "net_sales"
                                                                          ]
                                                                          : "No Sales"}
                                                                      </p>
                                                                    </div>

                                                                    <hr className="featurette-divider" />
                                                                    <span className="fw-bold">
                                                                      Scraper Jobs
                                                                    </span>
                                                                    {report?.[
                                                                      "scraper_jobs"
                                                                    ]?.map(
                                                                      (job) => {
                                                                        return (
                                                                          <Card>
                                                                            <Card.Header
                                                                              style={{
                                                                                textTransform:
                                                                                  "capitalize",
                                                                                border:
                                                                                  job.job_status ===
                                                                                    "in progress" ||
                                                                                    job.job_status.toLowerCase() ===
                                                                                    "scheduled"
                                                                                    ? "2px solid #DCAF0D"
                                                                                    : job.job_status ===
                                                                                      "stopped"
                                                                                      ? "2px solid #D14E4E"
                                                                                      : "2px solid #348355",
                                                                                borderCollapse:
                                                                                  "collapse",
                                                                              }}
                                                                            >
                                                                              <div className="d-flex justify-content-between align-items-center">
                                                                                {
                                                                                  job
                                                                                    ?.task_details
                                                                                    ?.schedule_type
                                                                                }{" "}
                                                                                -{" "}
                                                                                {
                                                                                  job
                                                                                    ?.scheduler_details
                                                                                    ?.pos_name
                                                                                }
                                                                                <span>
                                                                                  <TerminalFill
                                                                                    title="View Job Logs"
                                                                                    className="mx-2"
                                                                                    fill="gray"
                                                                                    style={{
                                                                                      cursor:
                                                                                        "pointer",
                                                                                    }}
                                                                                    onClick={() => {
                                                                                      setTaskIdForLogs(
                                                                                        job.task_id
                                                                                      );
                                                                                      setShowTaskLogsModal(
                                                                                        true
                                                                                      );
                                                                                      // navigate(
                                                                                      //   `/restaurants/${job.job_id}/scrapper-processes/job-logs/`
                                                                                      // );
                                                                                    }}
                                                                                  />
                                                                                  {job.job_status !==
                                                                                    "in progress" &&
                                                                                    new Date(
                                                                                      `${job?.task_details?.execution_date} ${job?.task_details?.execution_time}`
                                                                                    ) <
                                                                                    new Date()}
                                                                                  <ArrowRepeat
                                                                                    title="Retry Job"
                                                                                    style={{
                                                                                      cursor:
                                                                                        "pointer",
                                                                                    }}
                                                                                    onClick={() => {
                                                                                      setTaskIdToStart(
                                                                                        job.task_id
                                                                                      );
                                                                                    }}
                                                                                  />
                                                                                  {job
                                                                                    ?.task_details
                                                                                    ?.screenshot
                                                                                    ?.length >
                                                                                    0 &&
                                                                                    job?.task_details.screenshot.every(
                                                                                      (
                                                                                        it
                                                                                      ) =>
                                                                                        it !==
                                                                                        null
                                                                                    ) && (
                                                                                      <EyeFill
                                                                                        title="View Screenshots"
                                                                                        className="mx-2"
                                                                                        fill="gray"
                                                                                        style={{
                                                                                          cursor:
                                                                                            "pointer",
                                                                                        }}
                                                                                        onClick={() => {
                                                                                          openScreenshots(job.task_id);
                                                                                        }}
                                                                                      />
                                                                                    )}
                                                                                </span>
                                                                              </div>
                                                                            </Card.Header>
                                                                            <Card.Body
                                                                              style={{
                                                                                padding: 7,
                                                                                border:
                                                                                  job.job_status ===
                                                                                    "in progress" ||
                                                                                    job.job_status.toLowerCase() ===
                                                                                    "scheduled"
                                                                                    ? "2px solid #DCAF0D"
                                                                                    : job.job_status ===
                                                                                      "stopped"
                                                                                      ? "2px solid #D14E4E"
                                                                                      : "2px solid #348355",
                                                                                borderCollapse:
                                                                                  "collapse",
                                                                              }}
                                                                            >
                                                                              <div className="d-flex justify-content-between align-items-center">
                                                                                <p className="mb-0">Job Status</p>
                                                                                <div className="d-flex align-items-center gap-2">
                                                                                  {editingJobTaskId === job.task_id ? (
                                                                                    <>
                                                                                      <select
                                                                                        className="form-select form-select-sm"
                                                                                        style={{ fontSize: "12px", width: "130px" }}
                                                                                        value={selectedNewStatus}
                                                                                        onChange={(e) => setSelectedNewStatus(e.target.value)}
                                                                                      >
                                                                                        <option value="" disabled>Select status</option>
                                                                                        {["in progress", "scheduled", "completed", "failed", "stopped"].map((s) => (
                                                                                          <option key={s} value={s}>{s}</option>
                                                                                        ))}
                                                                                      </select>
                                                                                      <button
                                                                                        className="btn btn-success btn-sm py-0 px-2"
                                                                                        style={{ fontSize: "11px" }}
                                                                                        disabled={!selectedNewStatus || updatingJobStatus}
                                                                                        onClick={() => {
                                                                                          updateJobStatus(
                                                                                            { task_id: job.task_id, job_status: selectedNewStatus, report_date: date, restaurant_id: rest_id },
                                                                                            {
                                                                                              onSuccess: () => {
                                                                                                queryClient.invalidateQueries({ queryKey: ["master-report-status", date] });
                                                                                                setEditingJobTaskId(null);
                                                                                                setSelectedNewStatus("");
                                                                                              },
                                                                                            }
                                                                                          );
                                                                                        }}
                                                                                      >
                                                                                        {updatingJobStatus ? "..." : "Save"}
                                                                                      </button>
                                                                                      <button
                                                                                        className="btn btn-secondary btn-sm py-0 px-2"
                                                                                        style={{ fontSize: "11px" }}
                                                                                        onClick={() => {
                                                                                          setEditingJobTaskId(null);
                                                                                          setSelectedNewStatus("");
                                                                                        }}
                                                                                      >
                                                                                        ✕
                                                                                      </button>
                                                                                    </>
                                                                                  ) : (
                                                                                    <>
                                                                                      <p className="mb-0" style={{ textTransform: "capitalize" }}>
                                                                                        {job["job_status"]}
                                                                                      </p>
                                                                                      <PencilFill
                                                                                        title="Edit Job Status"
                                                                                        size={11}
                                                                                        fill="gray"
                                                                                        style={{ cursor: "pointer" }}
                                                                                        onClick={() => {
                                                                                          setEditingJobTaskId(job.task_id);
                                                                                          setSelectedNewStatus(job["job_status"]);
                                                                                        }}
                                                                                      />
                                                                                    </>
                                                                                  )}
                                                                                </div>
                                                                              </div>
                                                                              <div className="d-flex justify-content-between">
                                                                                <p>
                                                                                  Start
                                                                                  Time
                                                                                </p>
                                                                                <p
                                                                                  style={{
                                                                                    textTransform:
                                                                                      "capitalize",
                                                                                  }}
                                                                                >
                                                                                  {
                                                                                    job[
                                                                                    "job_start_time"
                                                                                    ]
                                                                                  }
                                                                                </p>
                                                                              </div>
                                                                              <div className="d-flex justify-content-between">
                                                                                <p>
                                                                                  End
                                                                                  Time
                                                                                </p>
                                                                                <p
                                                                                  style={{
                                                                                    textTransform:
                                                                                      "capitalize",
                                                                                  }}
                                                                                >
                                                                                  {
                                                                                    job[
                                                                                    "job_end_time"
                                                                                    ]
                                                                                  }
                                                                                </p>
                                                                              </div>
                                                                              <div className="d-flex justify-content-between">
                                                                                <p>
                                                                                  Extraction
                                                                                  Type
                                                                                </p>
                                                                                <p
                                                                                  style={{
                                                                                    textTransform:
                                                                                      "capitalize",
                                                                                  }}
                                                                                >
                                                                                  {
                                                                                    job[
                                                                                    "task_details"
                                                                                    ]?.[
                                                                                    "extraction_type"
                                                                                    ]
                                                                                  }
                                                                                </p>
                                                                              </div>
                                                                              <div className="d-flex justify-content-between">
                                                                                <p>
                                                                                  Execution
                                                                                  Date
                                                                                </p>
                                                                                <p
                                                                                  style={{
                                                                                    textTransform:
                                                                                      "capitalize",
                                                                                  }}
                                                                                >
                                                                                  {
                                                                                    job[
                                                                                    "task_details"
                                                                                    ]?.[
                                                                                    "execution_date"
                                                                                    ]
                                                                                  }
                                                                                </p>
                                                                              </div>
                                                                              <div className="d-flex justify-content-between">
                                                                                <p>
                                                                                  Execution
                                                                                  Time
                                                                                </p>
                                                                                <p
                                                                                  style={{
                                                                                    textTransform:
                                                                                      "capitalize",
                                                                                  }}
                                                                                >
                                                                                  {
                                                                                    job[
                                                                                    "task_details"
                                                                                    ]?.[
                                                                                    "execution_time"
                                                                                    ]
                                                                                  }
                                                                                </p>
                                                                              </div>
                                                                            </Card.Body>
                                                                          </Card>
                                                                        );
                                                                      }
                                                                    )}
                                                                  </>
                                                                );
                                                              }
                                                            )}
                                                          </Accordion.Body>
                                                        </Accordion.Item>
                                                      </Accordion>
                                                    )}
                                                </div>
                                              );
                                            }
                                          )}
                                      </div>
                                    </div>
                                  </Accordion.Body>
                                </Accordion.Item>
                              </Accordion>
                              {/* </p> */}
                            </div>
                          )}

                          {colEmailSubRestIds.length > 0 && (
                            <div className="text-sm-center px-1  ">
                              <Accordion className="my-2" defaultActiveKey={null} style={{ "--category-accent": "#7E57C2" }}>
                                <Accordion.Item eventKey="1">
                                  <Accordion.Header className="custom-header-email-sub">
                                    <div>Email Subscription ({colEmailSubRestIds.length})</div>
                                  </Accordion.Header>
                                  <Accordion.Body
                                    mountOnEnter
                                    unmountOnExit
                                    style={{
                                      padding: 0,
                                      textAlign: "left",
                                    }}
                                  >
                                    <div>
                                      <div key={date}>
                                        {data &&
                                          colEmailSubRestIds.map((rest_id, i) => (
                                            <div className="mx-2" key={i}>
                                              {renderRestaurantAccordion(rest_id, i, date)}
                                            </div>
                                          ))}
                                      </div>
                                    </div>
                                  </Accordion.Body>
                                </Accordion.Item>
                              </Accordion>
                            </div>
                          )}

                          {colManuallyUploadedRestIds.length > 0 && (
                            <div className="text-sm-center px-1  ">
                              <Accordion className="my-2" defaultActiveKey={null} style={{ "--category-accent": "#d60f79" }}>
                                <Accordion.Item eventKey="1">
                                  <Accordion.Header className="custom-header-manual-upload">
                                    <div>Manually Uploaded ({colManuallyUploadedRestIds.length})</div>
                                  </Accordion.Header>
                                  <Accordion.Body
                                    mountOnEnter
                                    unmountOnExit
                                    style={{
                                      padding: 0,
                                      textAlign: "left",
                                    }}
                                  >
                                    <div>
                                      <div key={date}>
                                        {data &&
                                          colManuallyUploadedRestIds.map((rest_id, i) => (
                                            <div className="mx-2" key={i}>
                                              {renderRestaurantAccordion(rest_id, i, date)}
                                            </div>
                                          ))}
                                      </div>
                                    </div>
                                  </Accordion.Body>
                                </Accordion.Item>
                              </Accordion>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div
                          style={{ height: "60vh", overflow: "auto" }}
                          className="hide-scrollbar"
                        >
                          <div>
                            <div key={date}>
                              {data &&
                                Object.keys(data?.data[date])
                                  ?.filter((_rest_id) => {
                                    return (
                                      data?.data[date][_rest_id][0]
                                        ?.scraper_jobs?.length > 0 &&
                                      data?.data[date][
                                        _rest_id
                                      ][0]?.scraper_jobs?.some(
                                        (it) => it?.["task_details"]
                                      )
                                    );
                                  })
                                  ?.map((rest_id, i) => {
                                    return (
                                      <div className="mx-2">
                                        {data?.data[date][rest_id]?.length >
                                          0 && (
                                            <Accordion
                                              defaultActiveKey={i}
                                              className="my-2 h-75"
                                            >
                                              <Accordion.Item eventKey="0">
                                                <Accordion.Header
                                                  title={
                                                    data.id_name_map?.[rest_id]
                                                  }
                                                >
                                                  <p
                                                    style={{
                                                      paddingTop: 15,
                                                      fontSize: "13px",
                                                    }}
                                                  >
                                                    {data.id_name_map?.[rest_id]
                                                      .length > 20
                                                      ? data.id_name_map?.[
                                                        rest_id
                                                      ]?.slice(0, 20) + "..."
                                                      : data.id_name_map?.[
                                                      rest_id
                                                      ]}
                                                  </p>
                                                </Accordion.Header>
                                                <Accordion.Body
                                                  style={{
                                                    textAlign: "left",
                                                  }}
                                                >
                                                  {data?.data[date][rest_id]?.map(
                                                    (report, rindx) => {
                                                      return (
                                                        <div key={rindx}>
                                                          {report[
                                                            "scraper_jobs"
                                                          ]?.map((r, i) => {
                                                            const backgroundColor =
                                                              r[
                                                                "job_status"
                                                              ]?.toLowerCase() ===
                                                                "in progress"
                                                                ? "#348355"
                                                                : r[
                                                                  "job_status"
                                                                ]?.toLowerCase() ===
                                                                  "scheduled"
                                                                  ? "#DCAF0D"
                                                                  : "#DCAF0D";

                                                            const style = {
                                                              backgroundColor:
                                                                backgroundColor,
                                                              color: "white",
                                                              fontSize: "12px",
                                                              padding:
                                                                "2px 8px 2px 8px",
                                                              borderRadius: 10,
                                                              cursor: "pointer",
                                                              textTransform:
                                                                "capitalize",
                                                            };
                                                            return (
                                                              <div
                                                                key={i}
                                                                style={{
                                                                  display: "flex",
                                                                  flexDirection:
                                                                    "row",
                                                                  justifyContent:
                                                                    "space-between",
                                                                  alignItems:
                                                                    "center",
                                                                }}
                                                              >
                                                                <p
                                                                  style={{
                                                                    textTransform:
                                                                      "capitalize",
                                                                    fontSize:
                                                                      "12px",
                                                                  }}
                                                                >
                                                                  {`${r?.["task_details"]?.["schedule_type"]} - 
                                                                    ${r?.["scheduler_details"]?.["pos_name"]}`}

                                                                  {r[
                                                                    "job_status"
                                                                  ]?.toLowerCase() ==
                                                                    "scheduled" ||
                                                                    (r[
                                                                      "job_status"
                                                                    ]?.toLowerCase() !==
                                                                      "scheduled" && (
                                                                        <>
                                                                          <OverlayTrigger
                                                                            placement="left"
                                                                            overlay={
                                                                              <Tooltip id="tooltip">
                                                                                {r?.[
                                                                                  "task_details"
                                                                                ]?.[
                                                                                  "extraction_type"
                                                                                ] ===
                                                                                  "daily" ||
                                                                                  r?.[
                                                                                  "task_details"
                                                                                  ]?.[
                                                                                  "extraction_type"
                                                                                  ] ===
                                                                                  "default" ? (
                                                                                  <>
                                                                                    <strong>
                                                                                      Date
                                                                                      List:{" "}
                                                                                    </strong>{" "}
                                                                                    {formatDate(
                                                                                      r?.[
                                                                                      "task_details"
                                                                                      ]?.[
                                                                                      "date_list"
                                                                                      ][0][0],
                                                                                      report?.[
                                                                                      "date_format"
                                                                                      ]
                                                                                    )}
                                                                                  </>
                                                                                ) : r?.[
                                                                                  "task_details"
                                                                                ]?.[
                                                                                  "extraction_type"
                                                                                ] ===
                                                                                  "summary" ? (
                                                                                  <>
                                                                                    {formatDate(
                                                                                      r?.[
                                                                                      "task_details"
                                                                                      ]?.[
                                                                                      "date_list"
                                                                                      ][0][0],
                                                                                      report?.[
                                                                                      "date_format"
                                                                                      ]
                                                                                    )}{" "}
                                                                                    to{" "}
                                                                                    {formatDate(
                                                                                      r?.[
                                                                                      "task_details"
                                                                                      ]?.[
                                                                                      "date_list"
                                                                                      ][0][1],
                                                                                      report?.[
                                                                                      "date_format"
                                                                                      ]
                                                                                    )}
                                                                                  </>
                                                                                ) : r?.[
                                                                                  "task_details"
                                                                                ]?.[
                                                                                  "extraction_type"
                                                                                ] ===
                                                                                  "range" ? (
                                                                                  <>
                                                                                    {formatDate(
                                                                                      r?.[
                                                                                      "task_details"
                                                                                      ]?.[
                                                                                      "date_list"
                                                                                      ][0][0],
                                                                                      report?.[
                                                                                      "date_format"
                                                                                      ]
                                                                                    )}{" "}
                                                                                    to{" "}
                                                                                    {formatDate(
                                                                                      r?.[
                                                                                      "task_details"
                                                                                      ]?.[
                                                                                      "date_list"
                                                                                      ][
                                                                                      r?.[
                                                                                        "task_details"
                                                                                      ]?.[
                                                                                        "date_list"
                                                                                      ]
                                                                                        .length -
                                                                                      1
                                                                                      ][0],
                                                                                      report?.[
                                                                                      "date_format"
                                                                                      ]
                                                                                    )}
                                                                                  </>
                                                                                ) : null}
                                                                              </Tooltip>
                                                                            }
                                                                          >
                                                                            <ExclamationCircleFill
                                                                              size={
                                                                                10
                                                                              }
                                                                              fill="gray"
                                                                              className="mx-2"
                                                                              style={{
                                                                                cursor:
                                                                                  "pointer",
                                                                              }}
                                                                            />
                                                                          </OverlayTrigger>
                                                                        </>
                                                                      ))}
                                                                </p>
                                                                <p
                                                                  style={style}
                                                                  onClick={() => {
                                                                    if (
                                                                      r[
                                                                        "job_status"
                                                                      ]?.toLowerCase() ===
                                                                      "stopped" ||
                                                                      r[
                                                                      "job_status"
                                                                      ] ===
                                                                      "completed"
                                                                    ) {
                                                                      return;
                                                                    }
                                                                    setTaskIdToStop(
                                                                      r["task_id"]
                                                                    );
                                                                  }}
                                                                >
                                                                  {r[
                                                                    "job_status"
                                                                  ] === "stopped"
                                                                    ? "Scheduled"
                                                                    : r[
                                                                    "job_status"
                                                                    ]}
                                                                </p>
                                                              </div>
                                                            );
                                                          })}
                                                        </div>
                                                      );
                                                    }
                                                  )}
                                                </Accordion.Body>
                                              </Accordion.Item>
                                            </Accordion>
                                          )}
                                      </div>
                                    );
                                  })}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>
        )}
        {!showCalendricView && <PosStatus />}
      </div>

      <Modal
        show={
          taskIdToStop !== null ||
          schedulerIdToStop !== null ||
          taskIdToStart !== null
        }
        onHide={() => {
          setTaskIdToStop(null);
          setSchedulerIdToStop(null);
          setTaskIdToStart(null);
        }}
        backdrop="static"
        keyboard={false}
        centered
        size="md"
      >
        <Modal.Header closeButton>
          <Modal.Title>Warning</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {taskIdToStop || schedulerIdToStop ? (
            <>
              <div className="text-center">
                <ExclamationTriangle size={50} color="red" className="my-2" />
              </div>
              <p className="text-center">
                Are you sure you want to stop this
                {taskIdToStop ? " task" : " scheduler"}?
              </p>
            </>
          ) : (
            <>
              <div className="text-center">
                <PlayBtnFill size={50} color="green" className="my-2" />
              </div>
              <p className="text-center">
                Are you sure you want to retry this task?
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn btn-secondary"
            onClick={() => {
              setTaskIdToStop(null);
              setSchedulerIdToStop(null);
              setTaskIdToStart(null);
            }}
          >
            Cancel
          </button>
          <button
            className={`btn btn-${taskIdToStop || schedulerIdToStop ? "danger" : "success"
              }`}
            onClick={() => {
              taskIdToStop
                ? handleStopTask(taskIdToStop)
                : taskIdToStart
                  ? handleStartTask(taskIdToStart)
                  : handleStopScheduler(schedulerIdToStop);
              setTaskIdToStop(null);
              setSchedulerIdToStop(null);
              setTaskIdToStart(null);
            }}
          >
            {taskIdToStop || schedulerIdToStop ? "Stop" : "Retry"}
          </button>
        </Modal.Footer>
      </Modal>
      <LogsModal
        show={showLogsModal}
        setShow={setShowLogsModal}
        id={jobIdForLogs}
      />
      <TaskLogsModal
        show={showTaskLogsModal}
        setShow={setShowTaskLogsModal}
        id={taskIdForLogs}
      />
      <ScreenShotsModal
        show={showScreenShotsModal}
        setShow={setShowScreenShotsModal}
        links={screenshots}
        setLinks={setScreenshots}
      />
      <SyncLogsModal
        show={showSyncLogsModal}
        setShow={setShowSyncLogsModal}
        logs={syncLogs}
      />
    </>
  );
};

export default MasterReportStatus;